import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Trophy, Printer, RefreshCcw, Loader2, Moon, Sun, Eye, EyeOff, Lock, Download } from 'lucide-react';
import { motion } from 'framer-motion';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { fetchWithAuth, AWARDS_API, REGISTRATION_RULES_API } from '../utils/api';
import { getTemplateById } from '../constants/awardTypeTemplates';
import { validateTabulatorPassword, storeTabulatorAuth, checkTabulatorAuth } from '../config/passwordConfig';

// Import shared UI components
import StageBackground from '../components/ui/StageBackground';
import DanceParticles from '../components/ui/DanceParticles';
import StageCard from '../components/ui/StageCard';

// Import new printing system
import PrintTemplate from '../components/printing/PrintTemplate';
import PrintLayoutFactory from '../components/printing/PrintLayoutFactory';
import '../components/printing/PrintStyles.css';

// Import shared types
import { AwardedRoutine, FlexibleScoreData, ReportType } from '../types/awards';

// Award interface
interface Award {
  name: string;
  color: string;
  minScore: number;
  maxScore: number;
}

const AwardsVisualizerPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { competitionCode } = useParams<{ competitionCode: string }>();
  const { theme } = useTheme();
  const { isAuthenticated, role, isLoading: authLoading } = useAuth();
  const [competitionCategories, setCompetitionCategories] = useState<any[]>([]);
  const [routines, setRoutines] = useState<AwardedRoutine[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [competitionName, setCompetitionName] = useState<string>('');
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const isDarkMode = theme === 'dark';
  const [showScores, setShowScores] = useState<boolean>(true); // Toggle for showing/hiding scores
  const [reportType, setReportType] = useState<ReportType>('adjudication'); // Report type selection
  const [customAwards, setCustomAwards] = useState<any[]>([]);
  const [availableReports, setAvailableReports] = useState<{value: string, label: string}[]>([]);
  
  // Awards configuration state
  const [awards, setAwards] = useState<Award[]>([]);
  
  // Stage filtering state
  const [selectedStages, setSelectedStages] = useState<string[]>(['all']); // Default to show all stages
  const [availableStages, setAvailableStages] = useState<{id: string, name: string}[]>([]);
  
  // Winners selection state for manual curation
  const [isWinnersMode, setIsWinnersMode] = useState<boolean>(false);
  const [selectedWinners, setSelectedWinners] = useState<AwardedRoutine[]>([]);
  const [eligibleRoutines, setEligibleRoutines] = useState<AwardedRoutine[]>([]);

  // Password protection state
  const [showPasswordModal, setShowPasswordModal] = useState<boolean>(false);
  const [password, setPassword] = useState<string>('');
  const [passwordError, setPasswordError] = useState<string>('');
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [isAuthorized, setIsAuthorized] = useState<boolean>(false);

  // Confirmation modal state
  const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);
  const [confirmAction, setConfirmAction] = useState<(() => void) | null>(null);
  
  // Save notification state
  const [saveNotification, setSaveNotification] = useState<{show: boolean, message: string, type: 'success' | 'error'}>({
    show: false,
    message: '',
    type: 'success'
  });

  // Auto-hide notification after 3 seconds
  useEffect(() => {
    if (saveNotification.show) {
      const timer = setTimeout(() => {
        setSaveNotification(prev => ({ ...prev, show: false }));
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [saveNotification.show]);

  // Check authentication - must be signed in to access
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      console.log('🚫 Awards Visualizer: User not authenticated, redirecting to login');
      navigate('/auth?state=signIn');
    }
  }, [authLoading, isAuthenticated, navigate]);

  // Check tabulator password authorization on component mount
  useEffect(() => {
    const isAuthorized = checkTabulatorAuth();
    
    if (!isAuthorized) {
      // Show password modal instead of redirecting
      setShowPasswordModal(true);
      setIsAuthorized(false);
    } else {
      setIsAuthorized(true);
    }
  }, []);

  // Handle password verification
  const handlePasswordSubmit = () => {
    if (validateTabulatorPassword(password)) {
      // Store tabulator authorization in session
      storeTabulatorAuth();
      
      setShowPasswordModal(false);
      setPassword('');
      setPasswordError('');
      setIsAuthorized(true);
    } else {
      setPasswordError('Incorrect password. Please contact your administrator.');
      setPassword('');
    }
  };
  
  // Helper function to determine category order for high score reports
  const getCategoryOrder = (category: string): number => {
    const categoryOrderMap: { [key: string]: number } = {
      'Solo': 1,
      'Duo': 2,
      'Trio': 2, // Same as Duo
      'Duo/Trio': 2,
      'Small Group': 3,
      'Large Group': 4,
      'Line': 5,
      'Extended Line': 6,
      'Ext. Line': 6,
      'Production': 7
    };
    
    return categoryOrderMap[category] || 999; // Unknown categories go to the end
  };

  // Helper function to extract stage ID from competition code if not provided in routine data
  const extractStageIdFromCompetitionCode = (compCode: string): string => {
    const match = compCode?.match(/_(\d{3})$/);
    return match ? match[1] : '001'; // Default to stage 001 if no match
  };

  // Helper function to get stage name from stage ID
  const getStageNameFromId = (stageId?: string): string => {
    if (!stageId) return 'Main Stage';
    const stageNum = parseInt(stageId);
    return `Stage ${stageNum}`;
  };

  // Helper function to extract available stages from routines data
  const extractAvailableStages = (routinesData: AwardedRoutine[]): {id: string, name: string}[] => {
    const stagesMap = new Map<string, string>();
    
    routinesData.forEach(routine => {
      if (routine.stageId && routine.stageName) {
        stagesMap.set(routine.stageId, routine.stageName);
      }
    });
    
    // If no stages found but we have routines, create a default stage
    if (stagesMap.size === 0 && routinesData.length > 0) {
      const defaultStageId = extractStageIdFromCompetitionCode(competitionCode || '');
      const defaultStageName = getStageNameFromId(defaultStageId);
      stagesMap.set(defaultStageId, defaultStageName);
    }
    
    // Convert to array and sort by stage ID
    return Array.from(stagesMap.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => parseInt(a.id) - parseInt(b.id));
  };

  // Helper function to filter routines by selected stages
  const filterRoutinesByStages = (routinesData: AwardedRoutine[]): AwardedRoutine[] => {
    if (selectedStages.includes('all')) {
      return routinesData;
    }
    
    return routinesData.filter(routine => 
      routine.stageId && selectedStages.includes(routine.stageId)
    );
  };

  // Helper function to handle stage filter changes
  const handleStageFilterChange = (stageId: string) => {
    if (stageId === 'all') {
      setSelectedStages(['all']);
    } else {
      // For single-select dropdown, always replace with the selected stage
      setSelectedStages([stageId]);
    }
  };

  // Helper function to get studio name (similar to RoutineMetadataCard)
  const getStudioName = (scheduleRoutine: any): string => {
    // Try studioName first, then studioCode, then fallback
    if (scheduleRoutine.studioName) return scheduleRoutine.studioName;
    
    // Try to get from enhanced data structure
    try {
      const enhancedStudioName = scheduleRoutine.parentRoutine?.submissionRoutines?.[0]?.registrations?.studios?.businessName;
      if (enhancedStudioName) return enhancedStudioName;
    } catch (error) {
      // Continue to fallback
    }
    
    // Fallback to studioCode if no name available
    return scheduleRoutine.studioCode || 'Unknown Studio';
  };

  // Helper function to get a simplified label for split columns
  const getSplitColumnLabel = (divisions: string[]): string => {
    if (divisions.length === 0) return '';
    if (divisions.length === 1) return divisions[0];
    
    // Priority order for preferred labels when multiple divisions are present
    const preferredLabels = ['Large Group', 'Small Group', 'Line', 'Ext. Line', 'Extended Line'];
    
    // Check if any preferred label is in the divisions
    for (const preferred of preferredLabels) {
      if (divisions.includes(preferred)) {
        return preferred;
      }
    }
    
    // Fallback: join all divisions with commas
    return divisions.join(', ');
  };

  // Helper function to check if a studio meets eligibility criteria for Studio Standouts
  const checkStudioEligibility = (studioName: string, allScheduleData: any[], eligibilityCriteria: any): boolean => {
    if (!eligibilityCriteria) return true; // If no criteria, all studios are eligible
    
    const minTotalRoutines = parseInt(eligibilityCriteria.minTotalRoutines) || 0;
    const minGroupRoutines = parseInt(eligibilityCriteria.minGroupRoutines) || 0;
    
    // Count all routines for this studio across all stages
    const studioRoutines = allScheduleData.filter(routine => {
      const routineStudioName = getStudioName(routine);
      return routineStudioName === studioName;
    });
    
    const totalRoutines = studioRoutines.length;
    
    // Count group routines (exclude Solo and Duo/Trio)
    const groupRoutines = studioRoutines.filter(routine => {
      const division = routine.division;
      return division !== 'Solo' && 
             division !== 'Duo/Trio' && 
             division !== 'Duo' && 
             division !== 'Trio';
    }).length;
    
    console.log(`Studio eligibility check for "${studioName}": ${totalRoutines} total routines, ${groupRoutines} group routines (need ${minTotalRoutines} total, ${minGroupRoutines} group)`);
    
    return totalRoutines >= minTotalRoutines && groupRoutines >= minGroupRoutines;
  };

  // Helper function to get excluded divisions for a report type
  const getExcludedDivisions = (reportType: string): string[] => {
    // Find the corresponding custom award configuration
    const customAward = customAwards.find(award => {
      if (!award.enabled) return false;
      
      // Check if this report matches the award type and variant
      if (reportType.startsWith(award.templateType)) {
        return true;
      }
      
      // Also check for variant-specific matches
      const template = getTemplateById(award.templateType);
      if (template?.supportsVariants && award.config.activeVariants) {
        return award.config.activeVariants.some((variantId: string) => 
          reportType === `${award.templateType}_${variantId}` || 
          reportType.startsWith(`${award.templateType}_${variantId}`)
        );
      }
      
      return false;
    });
    
    if (customAward?.config?.excludedDivisions) {
      return customAward.config.excludedDivisions;
    }
    
    return [];
  };

  // Helper function to get excluded competition categories for a report type
  const getExcludedCompetitionCategories = (reportType: string): string[] => {
    // Find the corresponding custom award configuration
    const customAward = customAwards.find(award => {
      if (!award.enabled) return false;
      
      // Check if this report matches the award type and variant
      if (reportType.startsWith(award.templateType)) {
        return true;
      }
      
      // Also check for variant-specific matches
      const template = getTemplateById(award.templateType);
      if (template?.supportsVariants && award.config.activeVariants) {
        return award.config.activeVariants.some((variantId: string) => 
          reportType === `${award.templateType}_${variantId}` || 
          reportType.startsWith(`${award.templateType}_${variantId}`)
        );
      }
      
      return false;
    });
    
    if (customAward?.config?.excludedCompetitionCategories) {
      return customAward.config.excludedCompetitionCategories;
    }
    
    return [];
  };

  // Helper function to get excluded style categories for a report type
  const getExcludedStyleCategories = (reportType: string): string[] => {
    // Find the corresponding custom award configuration
    const customAward = customAwards.find(award => {
      if (!award.enabled) return false;
      
      // Check if this report matches the award type and variant
      if (reportType.startsWith(award.templateType)) {
        return true;
      }
      
      // Also check for variant-specific matches
      const template = getTemplateById(award.templateType);
      if (template?.supportsVariants && award.config.activeVariants) {
        return award.config.activeVariants.some((variantId: string) => 
          reportType === `${award.templateType}_${variantId}` || 
          reportType.startsWith(`${award.templateType}_${variantId}`)
        );
      }
      
      return false;
    });
    
    if (customAward?.config?.excludedStyleCategories) {
      return customAward.config.excludedStyleCategories;
    }
    
    return [];
  };

  // Helper function to check if a routine should be excluded based on division, competition category, or style category
  const shouldExcludeRoutine = (routine: any, reportType: string): boolean => {
    const excludedDivisions = getExcludedDivisions(reportType);
    if (excludedDivisions.length > 0) {
      // Check if the routine's category (which maps to division) is excluded
      const routineCategory = routine.division || routine.category; // Check both fields
      if (excludedDivisions.includes(routineCategory)) {
        console.log(`Excluding routine ${routine.routineNumber || routine.id} (division: ${routineCategory}) due to excludedDivisions setting`);
        return true;
      }
    }
    
    // Check competition category exclusions
    const excludedCompetitionCategories = getExcludedCompetitionCategories(reportType);
    if (excludedCompetitionCategories.length > 0) {
      // Get the routine's competition category (routine type)
      const routineType = routine.parentRoutine?.routineType || routine.routineType || routine.type;
      if (routineType) {
        // Find matching competition category by label or typeCode (same logic as getRoutineTypeConfig)
        const routineTypeLower = routineType.toLowerCase().trim();
        const matchingCategory = competitionCategories.find((cat: any) => {
          const catTypeCode = (cat.typeCode || '').toLowerCase().trim();
          // Exact match
          if (catTypeCode === routineTypeLower) {
            return true;
          }
          // Handle variant: "adjudicated_only" vs "adjudication_only"
          if ((routineTypeLower.includes('adjudicated') || routineTypeLower.includes('adjudication')) &&
              (catTypeCode.includes('adjudicated') || catTypeCode.includes('adjudication'))) {
            return true;
          }
          return false;
        });
        
        if (matchingCategory) {
          // Check if the category label matches any excluded category
          const categoryLabel = matchingCategory.label || matchingCategory.typeCode || matchingCategory.name;
          if (excludedCompetitionCategories.includes(categoryLabel)) {
            console.log(`Excluding routine ${routine.routineNumber || routine.id} (competition category: ${categoryLabel}) due to excludedCompetitionCategories setting`);
            return true;
          }
        }
      }
    }
    
    // Check style category exclusions
    const excludedStyleCategories = getExcludedStyleCategories(reportType);
    if (excludedStyleCategories.length > 0) {
      // Get the routine's style category (stored in category field for high score reports)
      const routineStyle = routine.category || routine.style || routine.styleCategory;
      if (routineStyle) {
        // Check if the routine's style matches any excluded style category
        if (excludedStyleCategories.includes(routineStyle)) {
          console.log(`Excluding routine ${routine.routineNumber || routine.id} (style category: ${routineStyle}) due to excludedStyleCategories setting`);
          return true;
        }
      }
    }
    
    return false;
  };

  // Helper function to get routine type configuration for a routine
  const getRoutineTypeConfig = (routine: any): any => {
    if (!routine || !competitionCategories || competitionCategories.length === 0) {
      return null;
    }
    
    // Get routine type from routine data
    const routineType = routine.parentRoutine?.routineType || routine.routineType || routine.type;
    if (!routineType) {
      return null;
    }
    
    // Look up the configuration from the custom competition categories (case-insensitive match)
    const routineTypeLower = routineType.toLowerCase().trim();
    const categoryConfig = competitionCategories.find((cat: any) => {
      const catTypeCode = (cat.typeCode || '').toLowerCase().trim();
      // Exact match
      if (catTypeCode === routineTypeLower) {
        return true;
      }
      // Handle variant: "adjudicated_only" vs "adjudication_only"
      if ((routineTypeLower.includes('adjudicated') || routineTypeLower.includes('adjudication')) &&
          (catTypeCode.includes('adjudicated') || catTypeCode.includes('adjudication'))) {
        // Both are adjudication-related, consider them a match
        return true;
      }
      return false;
    });
    
    if (categoryConfig) {
      console.log(`Found routine type config for "${routineType}":`, categoryConfig);
    } else {
      console.log(`No routine type config found for "${routineType}". Available typeCodes:`, 
        competitionCategories.map((cat: any) => cat.typeCode));
    }
    
    return categoryConfig || null;
  };

  // Helper function to check if a routine is eligible for a specific report type
  const isRoutineEligibleForReport = (routine: any, currentReportType: string): boolean => {
    // Adjudication awards always include all routines (including adjudicated-only)
    if (currentReportType === 'adjudication') {
      return true;
    }
    
    // Get routine type from routine data
    const routineType = routine.parentRoutine?.routineType || routine.routineType || routine.type;
    
    // If routine type contains "adjudicated" or "adjudication", be more strict about eligibility
    // This is a fallback in case competition categories aren't loaded yet
    if (routineType && (routineType.toLowerCase().includes('adjudicated') || routineType.toLowerCase().includes('adjudication'))) {
      // For high score reports, exclude adjudicated routines if we can't find config
      if (currentReportType === 'highScoreByAge' || currentReportType.startsWith('highScoreByAge') ||
          currentReportType === 'high-score-division' || currentReportType === 'highScoreByDivision' || currentReportType.startsWith('highScoreByDivision')) {
        // If competition categories are loaded, check the config
        if (competitionCategories && competitionCategories.length > 0) {
          const routineTypeConfig = getRoutineTypeConfig(routine);
          if (routineTypeConfig) {
            // Use the config value
            const isEligible = routineTypeConfig.eligibleForHighScore === true;
            console.log(`Routine ${routine.routineNumber || routine.id} (${routineType}) eligibility for ${currentReportType}:`, {
              eligibleForHighScore: routineTypeConfig.eligibleForHighScore,
              isEligible
            });
            return isEligible;
          } else {
            // Config not found but routine type suggests adjudicated - exclude from high scores
            console.log(`Routine ${routine.routineNumber || routine.id} (${routineType}) - adjudicated type but no config found, excluding from high scores`);
            return false;
          }
        } else {
          // Categories not loaded yet - exclude adjudicated routines from high scores to be safe
          console.log(`Routine ${routine.routineNumber || routine.id} (${routineType}) - adjudicated type, categories not loaded, excluding from high scores`);
          return false;
        }
      }
      
      // For special awards, check config if available
      if (currentReportType === 'greatestHits' || currentReportType.startsWith('greatestHits') ||
          currentReportType === 'studioStandout' || currentReportType.startsWith('studioStandout')) {
        if (competitionCategories && competitionCategories.length > 0) {
          const routineTypeConfig = getRoutineTypeConfig(routine);
          if (routineTypeConfig) {
            const isEligible = routineTypeConfig.eligibleForSpecialAwards === true;
            console.log(`Routine ${routine.routineNumber || routine.id} (${routineType}) eligibility for ${currentReportType}:`, {
              eligibleForSpecialAwards: routineTypeConfig.eligibleForSpecialAwards,
              isEligible
            });
            return isEligible;
          } else {
            console.log(`Routine ${routine.routineNumber || routine.id} (${routineType}) - adjudicated type but no config found, excluding from special awards`);
            return false;
          }
        } else {
          console.log(`Routine ${routine.routineNumber || routine.id} (${routineType}) - adjudicated type, categories not loaded, excluding from special awards`);
          return false;
        }
      }
    }
    
    // For non-adjudicated routines or if no routine type, get config and check
    const routineTypeConfig = getRoutineTypeConfig(routine);
    
    // If no config found, default to eligible (backward compatibility for normal routines)
    if (!routineTypeConfig) {
      return true;
    }
    
    // Check eligibility based on report type
    if (currentReportType === 'highScoreByAge' || currentReportType.startsWith('highScoreByAge') ||
        currentReportType === 'high-score-division' || currentReportType === 'highScoreByDivision' || currentReportType.startsWith('highScoreByDivision')) {
      // High score reports: must explicitly be true
      return routineTypeConfig.eligibleForHighScore === true;
    }
    
    if (currentReportType === 'greatestHits' || currentReportType.startsWith('greatestHits') ||
        currentReportType === 'studioStandout' || currentReportType.startsWith('studioStandout')) {
      // Special awards: must explicitly be true
      return routineTypeConfig.eligibleForSpecialAwards === true;
    }
    
    // For other report types, default to eligible
    return true;
  };

  // Helper function to check if current award type supports winners
  const supportsWinners = (): boolean => {
    const customAward = customAwards.find(award => {
      if (!award.enabled) return false;
      return reportType.startsWith(award.templateType);
    });
    
    if (customAward) {
      const template = getTemplateById(customAward.templateType);
      return (template as any)?.supportsWinners && customAward.config?.allowManualSelection;
    }
    
    return false;
  };

  // Helper function to add a routine to winners
  const addToWinners = (routine: AwardedRoutine) => {
    if (!selectedWinners.find(w => w.routineId === routine.routineId)) {
      setSelectedWinners(prev => [...prev, routine]);
    }
  };

  // Helper function to remove a routine from winners
  const removeFromWinners = (routineId: string) => {
    setSelectedWinners(prev => prev.filter(w => w.routineId !== routineId));
  };

  // Helper function to toggle winners mode
  const toggleWinnersMode = async () => {
    const newWinnersMode = !isWinnersMode;
    setIsWinnersMode(newWinnersMode);
    
    if (newWinnersMode) {
      // Entering winners mode - set eligible routines and auto-load saved winners
      let eligibleRoutines = routines;
      
      // For Studio Standouts, the eligibility check was already done in fetchAwardsData
      // so the routines array already contains only routines from eligible studios
      setEligibleRoutines(eligibleRoutines);
      
      // Auto-load saved winners
      console.log('Entering winners mode - auto-loading saved winners...');
      await loadSelectedWinners();
    } else {
      // Exiting winners mode - clear selections
      setSelectedWinners([]);
      setEligibleRoutines([]);
    }
  };

  // Winner persistence functions
  const saveSelectedWinners = async () => {
    if (!competitionCode || !reportType) {
      console.error('Missing competition code or report type');
      return false;
    }

    try {
      const stageId = selectedStages.includes('all') ? 'all' : selectedStages[0] || 'all';
      
      console.log(`Saving winners for ${competitionCode}, report: ${reportType}, stage: ${stageId}`, selectedWinners);
      
      const AWS_API_BASE_URL = process.env.REACT_APP_API_URL || 'https://auf2hpeud1.execute-api.us-east-2.amazonaws.com/dev';
      const endpoint = `/competition/${competitionCode}/winners`;
      
      const response = await fetch(`${AWS_API_BASE_URL}${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          reportType,
          stageId,
          selectedWinners
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(`Failed to save winners: ${response.status} - ${errorData.error || 'Unknown error'}`);
      }
      
      const result = await response.json();
      console.log('Winners saved successfully:', result);
      
      // Show success notification
      setSaveNotification({
        show: true,
        message: `${selectedWinners.length} winner${selectedWinners.length !== 1 ? 's' : ''} saved successfully!`,
        type: 'success'
      });
      
      return true;
    } catch (error) {
      console.error('Error saving winners:', error);
      
      // Show error notification
      setSaveNotification({
        show: true,
        message: `Failed to save winners: ${error instanceof Error ? error.message : 'Unknown error'}`,
        type: 'error'
      });
      
      return false;
    }
  };

  const loadSelectedWinners = async () => {
    if (!competitionCode || !reportType) {
      console.error('Missing competition code or report type');
      return false;
    }

    try {
      const stageId = selectedStages.includes('all') ? 'all' : selectedStages[0] || 'all';
      
      console.log(`Loading winners for ${competitionCode}, report: ${reportType}, stage: ${stageId}`);
      
      const AWS_API_BASE_URL = process.env.REACT_APP_API_URL || 'https://auf2hpeud1.execute-api.us-east-2.amazonaws.com/dev';
      const endpoint = `/competition/${competitionCode}/winners?reportType=${encodeURIComponent(reportType)}&stageId=${encodeURIComponent(stageId)}`;
      
      const response = await fetch(`${AWS_API_BASE_URL}${endpoint}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        if (response.status === 404) {
          // No winners found - this is normal for new competitions
          console.log('No saved winners found');
          setSelectedWinners([]);
          return true;
        }
        const errorData = await response.json();
        throw new Error(`Failed to load winners: ${response.status} - ${errorData.error || 'Unknown error'}`);
      }
      
      const result = await response.json();
      console.log('Winners loaded successfully:', result);
      
      // Set the loaded winners
      setSelectedWinners(result.selectedWinners || []);
      
      return true;
    } catch (error) {
      console.error('Error loading winners:', error);
      return false;
    }
  };

  const deleteSelectedWinners = () => {
    if (!competitionCode || !reportType) {
      console.error('Missing competition code or report type');
      return;
    }

    // Show custom confirmation modal
    setConfirmAction(() => async () => {
      try {
        const stageId = selectedStages.includes('all') ? 'all' : selectedStages[0] || 'all';
        
        console.log(`Deleting winners for ${competitionCode}, report: ${reportType}, stage: ${stageId}`);
        
        const AWS_API_BASE_URL = process.env.REACT_APP_API_URL || 'https://auf2hpeud1.execute-api.us-east-2.amazonaws.com/dev';
        const endpoint = `/competition/${competitionCode}/winners?reportType=${encodeURIComponent(reportType)}&stageId=${encodeURIComponent(stageId)}`;
        
        const response = await fetch(`${AWS_API_BASE_URL}${endpoint}`, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json'
          }
        });
        
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(`Failed to delete winners: ${response.status} - ${errorData.error || 'Unknown error'}`);
        }
        
        const result = await response.json();
        console.log('Winners deleted successfully:', result);
        
        // Clear local state
        setSelectedWinners([]);
        
        // Close modal
        setShowConfirmModal(false);
        setConfirmAction(null);
      } catch (error) {
        console.error('Error deleting winners:', error);
        // Close modal even on error
        setShowConfirmModal(false);
        setConfirmAction(null);
      }
    });
    
    setShowConfirmModal(true);
  };
  
  // Set page title
  useEffect(() => {
    document.title = 'Dance Digital | Awards Visualizer';
    return () => {
      document.title = 'Dance Digital'; // Reset on unmount
    };
  }, []);

  // Fetch competition categories for routine type filtering
  useEffect(() => {
    const loadCompetitionCategories = async () => {
      try {
        const data = await fetchWithAuth(REGISTRATION_RULES_API);
        const categories = data.competitionCategories || [];
        setCompetitionCategories(categories);
        console.log('Loaded competition categories:', categories);
      } catch (error) {
        console.error('Error loading competition categories:', error);
        setCompetitionCategories([]);
      }
    };
    
    loadCompetitionCategories();
  }, []);

  // Fetch custom awards configuration
  useEffect(() => {
    const loadCustomAwards = async () => {
      try {
        const data = await fetchWithAuth(AWARDS_API);
        const awards = data.customAwards || [];
        setCustomAwards(awards);
        console.log('Loaded custom awards:', awards);
        
        // Build available reports list dynamically
        const reports: {value: string, label: string}[] = [
          { value: 'adjudication', label: 'Adjudication Awards' }
        ];
        
        // Add enabled custom awards
        awards.forEach((award: any) => {
          if (!award.enabled) return;
          
          const template = getTemplateById(award.templateType);
          if (!template) return;
          
          // Add base award type
          if (template.supportsVariants && award.config.activeVariants) {
            // Add each active variant
            award.config.activeVariants.forEach((variantId: string) => {
              const variant = (template as any).defaultVariants?.find((v: any) => v.id === variantId);
              const variantName = award.config.variantNames?.[variantId] || variant?.name || '';
              const reportValue = `${award.templateType}-${variantId}`;
              const reportLabel = variantName ? `${award.displayName} (${variantName})` : award.displayName;
              reports.push({ value: reportValue, label: reportLabel });
            });
          } else {
            // Single report type
            reports.push({ value: award.templateType, label: award.displayName });
          }
          
          // Note: Winners variants are no longer separate dropdown entries
          // They are now handled as a mode toggle within the main award type
        });
        
        setAvailableReports(reports);
      } catch (error) {
        console.error('Error loading custom awards:', error);
        // Fallback to default reports
        setAvailableReports([
          { value: 'adjudication', label: 'Adjudication Awards' }
        ]);
      }
    };
    
    loadCustomAwards();
  }, []);

  // Load awards configuration for score correlation
  useEffect(() => {
    let isMounted = true;
    
    const loadAwards = async () => {
      try {
        // Fetch awards configuration with authentication (same as other components)
        const data = await fetchWithAuth(AWARDS_API);
        if (isMounted) {
          console.log('✅ Awards configuration loaded:', data.awards);
          setAwards(data.awards || []);
        }
      } catch (err) {
        if (isMounted) {
          console.error("Error loading awards:", err);
          console.warn('⚠️ Using default awards as fallback');
          // Set default awards on error
          setAwards([
            { name: 'Multi Platinum', color: '#FF1493', minScore: 291, maxScore: 300 },
            { name: 'Platinum', color: '#E5E4E2', minScore: 282, maxScore: 290 },
            { name: 'High Gold', color: '#FFD700', minScore: 273, maxScore: 281 },
            { name: 'Gold', color: '#DAA520', minScore: 264, maxScore: 272 },
            { name: 'High Silver', color: '#C0C0C0', minScore: 255, maxScore: 263 },
            { name: 'Silver', color: '#A8A8A8', minScore: 0, maxScore: 254 }
          ]);
        }
      }
    };
    
    loadAwards();
    return () => { isMounted = false; };
  }, []);
  
  // Get award for a score
  const getAwardForScore = (score: number): Award | null => {
    if (score === null || score === undefined || !awards?.length) {
      console.log('⚠️ getAwardForScore: No awards available!', { score, awardsLength: awards?.length, awards });
      return null;
    }
    const matchedAward = awards.find(award => score >= award.minScore && score <= award.maxScore) || awards[awards.length - 1];
    
    // Debug for routine 987 (score 285)
    if (score === 285) {
      console.log('🎯 getAwardForScore for 285:', { score, matchedAward, awards });
    }
    
    return matchedAward;
  };

  const fetchAwardsData = async () => {
    console.log('🎯 AWARDS DEBUG - fetchAwardsData called');
    console.log('🎯 AWARDS DEBUG - competitionCode:', competitionCode);
    
    if (!competitionCode) {
      console.log('❌ AWARDS DEBUG - No competition code, returning early');
      return;
    }
    
    setIsLoading(true);
    try {
      // Fetch data from the same endpoint as ReportsCardV2
      const AWS_API_BASE_URL = process.env.REACT_APP_API_URL || 'https://auf2hpeud1.execute-api.us-east-2.amazonaws.com/dev';
      const endpoint = `/competition/${competitionCode}/scores/all`;
      
      // Add cache-busting parameter to force fresh data
      const cacheBuster = `?t=${Date.now()}`;
      
      console.log('🎯 AWARDS DEBUG - Fetching from:', `${AWS_API_BASE_URL}${endpoint}${cacheBuster}`);
      
      const response = await fetch(`${AWS_API_BASE_URL}${endpoint}${cacheBuster}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      console.log('🎯 AWARDS DEBUG - Response status:', response.status, response.ok);
      
      if (!response.ok) throw new Error(`Failed to fetch scores: ${response.status}`);
      
      const data = await response.json();
      console.log('🎯 AWARDS DEBUG - Raw API response:', data);
      console.log('🎯 AWARDS DEBUG - scheduleData length:', (data.scheduleData || []).length);
      console.log('🎯 AWARDS DEBUG - scoreUpdates keys:', Object.keys(data.scoreUpdates || {}).length);
      
      // DEBUG: Check specifically for routine 987 keys in raw response
      const routine987Keys = Object.keys(data.scoreUpdates || {}).filter(k => k.includes('987'));
      console.log('🎯 AWARDS DEBUG - Routine 987 keys in raw response:', routine987Keys);
      
      // Helper function to get flexible scoring data (same as AwardsTracker)
      const getFlexibleScoreDataFromUpdates = (routineId: string, scoreUpdates: any): FlexibleScoreData | null => {
        const judgeScores: Array<{judgeId: string; totalScore: number;}> = [];
        
        // Debug logging for routine 987
        if (routineId === '987') {
          console.log('🔍 Checking scoreUpdates keys for routine 987:', Object.keys(scoreUpdates).filter(k => k.startsWith('987_')));
        }
        
        // Look for individual judge scores with pattern: routineId_judgeId
        Object.keys(scoreUpdates).forEach(key => {
          if (key.startsWith(`${routineId}_`) && !key.includes('flexible')) {
            const scoreData = scoreUpdates[key];
            
            // Debug logging for routine 987
            if (routineId === '987') {
              console.log(`🔍 Found score key: ${key}`, {
                isSubmitted: scoreData?.isSubmitted,
                scores: scoreData?.scores,
                simpleScore: scoreData?.scores?.simpleScore,
                fullScoreData: scoreData
              });
            }
            
            if (scoreData && scoreData.isSubmitted) {
              // Use simpleScore - try both top level and nested location
              const judgeScore = scoreData.simpleScore || scoreData.scores?.simpleScore || 0;
              
              judgeScores.push({
                judgeId: scoreData.judgeId || key.split('_')[1],
                totalScore: judgeScore
              });
            }
          }
        });

        // Debug logging for routine 987
        if (routineId === '987') {
          console.log('🔍 Final judgeScores for 987:', judgeScores);
        }

        // Need at least 3 scores to display
        if (judgeScores.length >= 3) {
          // Sort scores from highest to lowest
          const sortedScores = [...judgeScores].sort((a, b) => b.totalScore - a.totalScore);
          
          let finalScore = 0;
          let droppedScores: Array<{judgeId: string; totalScore: number; dropReason?: string}> = [];
          let appliedRules: string[] = [];
          
          if (sortedScores.length === 3) {
            // 3 judges: sum all scores, show 0 as dropped score for consistency
            finalScore = sortedScores.reduce((sum, score) => sum + score.totalScore, 0);
            droppedScores = [{
              judgeId: 'none',
              totalScore: 0,
              dropReason: 'No 4th judge'
            }];
            appliedRules.push('3 judges: all scores counted');
          } else if (sortedScores.length >= 4) {
            // 4+ judges: sum the highest 3, drop the lowest
            const topThree = sortedScores.slice(0, 3);
            const dropped = sortedScores.slice(3);
            
            finalScore = topThree.reduce((sum, score) => sum + score.totalScore, 0);
            droppedScores = dropped.map(score => ({
              judgeId: score.judgeId,
              totalScore: score.totalScore,
              dropReason: 'Lowest score dropped'
            }));
            appliedRules.push(`${sortedScores.length} judges: highest 3 scores counted, lowest ${dropped.length} dropped`);
          }
          
          // Get correlated award based on final score
          const award = getAwardForScore(finalScore);
          const correlatedAward = award ? award.name : 'No Award';
          
          return {
            finalScore,
            judgeScores: sortedScores.map(s => ({
              judgeId: s.judgeId,
              totalScore: s.totalScore,
              scores: {}, // Not available in simple format
              isSubmitted: true
            })) as any,
            droppedScores,
            appliedRules,
            correlatedAward
          };
        }

        return null;
      };
      
      // Wrapper function for backward compatibility
      const getFlexibleScoreData = (routineId: string): FlexibleScoreData | null => {
        const result = getFlexibleScoreDataFromUpdates(routineId, data.scoreUpdates || {});
        if (result && routineId === '987') {
          // Debug for the "Im A Lady" routine that shows wrong score
          console.log(`🔍 DETAILED Score lookup for routineId: ${routineId}`, {
            finalScore: result.finalScore,
            droppedScores: result.droppedScores,
            judgeScores: result.judgeScores,
            correlatedAward: result.correlatedAward
          });
        }
        return result;
      };
      
      // Helper function to get age division order based on min age
      const getAgeDivisionOrder = (ageGroup: string, allRoutines: any[]): number => {
        // For now, we'll use a basic ordering. In the future, this could fetch from HitchKick API
        // to get the actual minAge/maxAge values
        const ageOrderMap: { [key: string]: number } = {
          'PeeWee': 1,
          'Mini': 2,
          'Junior': 3,
          'Teen': 4,
          'Senior': 5,
          'Open': 6,
          'Streetz Stars': 1, // Based on your sample data
        };
        
        return ageOrderMap[ageGroup] || 999; // Unknown age groups go last
      };
      
      // Helper function to determine if category is solo (gets top 10 vs top 5)
      const isSoloCategory = (category: string): boolean => {
        return category.toLowerCase() === 'solo';
      };
      
      // Helper function to process high score by performance division data
      const processHighScoreByPerformanceDivision = (scheduleData: any[], reportType: string): AwardedRoutine[] => {
        const allScoredRoutines: AwardedRoutine[] = [];
        
        // Process each routine from schedule data
        scheduleData.forEach((scheduleRoutine: any) => {
          const routineId = scheduleRoutine.id || scheduleRoutine.routineNumber;
          const flexibleScoreData = getFlexibleScoreData(routineId);
          
          // Include all routines that have been scored (not just awarded ones)
          if (flexibleScoreData && flexibleScoreData.finalScore > 0) {
            // Check if this routine is eligible for this report type
            if (!isRoutineEligibleForReport(scheduleRoutine, reportType)) {
              console.log(`Excluding routine ${scheduleRoutine.routineNumber} - not eligible for ${reportType}`);
              return; // Skip this routine
            }
            
            // Check if this routine should be excluded based on division or competition category settings
            if (shouldExcludeRoutine(scheduleRoutine, reportType)) {
              return; // Skip this routine
            }
            
            const isSolo = scheduleRoutine.division === 'Solo';
            const routineTitle = scheduleRoutine.name || `Routine #${scheduleRoutine.routineNumber}`;
            const performerName = scheduleRoutine.performerName || '';
            
            // For solos, combine title and performer name (only if not already included)
            const displayTitle = isSolo && performerName && !routineTitle.includes(performerName)
              ? `${routineTitle} (${performerName})`
              : routineTitle;
            
            console.log(`Processing routine ${scheduleRoutine.routineNumber}: ${displayTitle}`, {
              finalScore: flexibleScoreData.finalScore,
              category: scheduleRoutine.category, // Contemporary, Jazz, etc. - this is what we group by for performance division
              division: scheduleRoutine.division, // Solo, Duo, etc. - this is the category for filtering
              ageGroup: scheduleRoutine.ageGroup,
              droppedScores: flexibleScoreData.droppedScores,
              judgeScores: flexibleScoreData.judgeScores
            });
            
            allScoredRoutines.push({
              routineId: scheduleRoutine.routineNumber,
              routineName: displayTitle,
              performer: performerName,
              studio: getStudioName(scheduleRoutine),
              division: scheduleRoutine.category || 'Unknown', // This is performance division (Contemporary, Jazz, etc.)
              category: scheduleRoutine.division || 'Solo', // This is what we filter by (Solo, Duo, etc.)
              ageGroup: scheduleRoutine.ageGroup || 'Unknown',
              finalScore: flexibleScoreData.finalScore,
              award: flexibleScoreData.correlatedAward, // Use the correlated award (Platinum, Gold, etc.)
              rank: 0, // Will be set based on placement
              type: scheduleRoutine.type || 'Competitive',
              flexibleScoreData: flexibleScoreData,
              placement: '', // Will be calculated
              stageId: scheduleRoutine.stageId || extractStageIdFromCompetitionCode(competitionCode!),
              stageName: scheduleRoutine.stageName || getStageNameFromId(scheduleRoutine.stageId)
            });
          }
        });
        
        // Group routines by age division, then by performance division
        const groupedRoutines: { [ageGroup: string]: { [performanceDivision: string]: AwardedRoutine[] } } = {};
        
        allScoredRoutines.forEach(routine => {
          if (!groupedRoutines[routine.ageGroup]) {
            groupedRoutines[routine.ageGroup] = {};
          }
          if (!groupedRoutines[routine.ageGroup][routine.division]) {
            groupedRoutines[routine.ageGroup][routine.division] = [];
          }
          groupedRoutines[routine.ageGroup][routine.division].push(routine);
        });
        
        // Process each age group and performance division
        const finalRoutines: AwardedRoutine[] = [];
        
        // Sort age groups by order (youngest to oldest)
        const sortedAgeGroups = Object.keys(groupedRoutines).sort((a, b) => 
          getAgeDivisionOrder(a, allScoredRoutines) - getAgeDivisionOrder(b, allScoredRoutines)
        );
        
        sortedAgeGroups.forEach(ageGroup => {
          // Sort performance divisions alphabetically (Contemporary, Hip-Hop, Jazz, Lyrical, Tap, etc.)
          const sortedPerformanceDivisions = Object.keys(groupedRoutines[ageGroup]).sort();
          
          sortedPerformanceDivisions.forEach(performanceDivision => {
            const divisionRoutines = groupedRoutines[ageGroup][performanceDivision];
            
            console.log(`Processing ${ageGroup} ${performanceDivision} - ${divisionRoutines.length} routines:`, 
              divisionRoutines.map(r => ({ name: r.routineName, score: r.finalScore, category: r.category }))
            );
            
            // Sort by score descending (highest to lowest), with drop score tie-breaking
            divisionRoutines.sort((a, b) => {
              // First compare final scores
              if (a.finalScore !== b.finalScore) {
                return b.finalScore - a.finalScore;
              }
              
              // If final scores are tied, use drop scores for tie-breaking
              const aDropScore = a.flexibleScoreData?.droppedScores?.[0]?.totalScore || 
                                a.flexibleScoreData?.droppedScores?.[0]?.originalScore?.totalScore || 0;
              const bDropScore = b.flexibleScoreData?.droppedScores?.[0]?.totalScore || 
                                b.flexibleScoreData?.droppedScores?.[0]?.originalScore?.totalScore || 0;
              
              console.log(`Tie-breaking: ${a.routineName} (${a.finalScore}, drop: ${aDropScore}) vs ${b.routineName} (${b.finalScore}, drop: ${bDropScore})`);
              
              return bDropScore - aDropScore; // Higher drop score wins
            });
            
            console.log(`After sorting ${ageGroup} ${performanceDivision}:`, 
              divisionRoutines.map(r => ({ name: r.routineName, score: r.finalScore }))
            );
            
            // Determine how many placements to include based on the variant
            let maxPlacements = 3; // Default to top 3 placements
            
            // Parse variant from reportType (e.g., "highScoreByDivision-top5")
            if (reportType.includes('-')) {
              const variant = reportType.split('-')[1];
              if (variant === 'top3') {
                maxPlacements = 3;
              } else if (variant === 'top5') {
                maxPlacements = 5;
              }
            }
            
            // Filter by top N placements (not top N routines)
            const routinesWithPlacements = filterByTopPlacements(divisionRoutines, maxPlacements);
            
            // Add to final routines
            routinesWithPlacements.forEach(routine => {
              // Keep the original correlated award from flexible scoring (Platinum, Gold, etc.)
              // routine.award already contains the correlated award from flexibleScoreData
              console.log(`Assigned ${routine.placement} to ${routine.routineName} (score: ${routine.finalScore})`);
              finalRoutines.push(routine);
            });
          });
        });
        
        // Sort final routines for display (10th, 9th, 8th... to 1st)
        // Higher placement numbers first (10 > 9 > 8 > ... > 1) for MC reading order
        finalRoutines.sort((a, b) => {
          // First sort by age division order (youngest to oldest)
          const ageOrderA = getAgeDivisionOrder(a.ageGroup, []);
          const ageOrderB = getAgeDivisionOrder(b.ageGroup, []);
          if (ageOrderA !== ageOrderB) return ageOrderA - ageOrderB;
          
          // Then by performance division order (alphabetical)
          if (a.division !== b.division) {
            return a.division.localeCompare(b.division);
          }
          
          // Finally by placement for display (higher placement numbers first: 10th, 9th, 8th, etc.)
          // For MC reading: start with 10th place, end with 1st place
          return b.rank - a.rank; // Higher rank (10th) comes before lower rank (1st)
        });
        
        return finalRoutines;
      };
      const processHighScoreByAge = (scheduleData: any[], reportType: string): AwardedRoutine[] => {
        const allScoredRoutines: AwardedRoutine[] = [];
        
        // Process each routine from schedule data
        scheduleData.forEach((scheduleRoutine: any) => {
          const routineId = scheduleRoutine.id || scheduleRoutine.routineNumber;
          const flexibleScoreData = getFlexibleScoreData(routineId);
          
          // Include all routines that have been scored (not just awarded ones)
          if (flexibleScoreData && flexibleScoreData.finalScore > 0) {
            // Check if this routine is eligible for this report type
            if (!isRoutineEligibleForReport(scheduleRoutine, reportType)) {
              console.log(`Excluding routine ${scheduleRoutine.routineNumber} - not eligible for ${reportType}`);
              return; // Skip this routine
            }
            
            // Check if this routine should be excluded based on division or competition category settings
            if (shouldExcludeRoutine(scheduleRoutine, reportType)) {
              return; // Skip this routine
            }
            
            const isSolo = scheduleRoutine.division === 'Solo';
            const routineTitle = scheduleRoutine.name || `Routine #${scheduleRoutine.routineNumber}`;
            const performerName = scheduleRoutine.performerName || '';
            
            // For solos, combine title and performer name (only if not already included)
            const displayTitle = isSolo && performerName && !routineTitle.includes(performerName)
              ? `${routineTitle} (${performerName})`
              : routineTitle;
            
            console.log(`Processing routine ${scheduleRoutine.routineNumber}: ${displayTitle}`, {
              finalScore: flexibleScoreData.finalScore,
              category: scheduleRoutine.category, // Contemporary, Jazz, etc. - this is performance style
              division: scheduleRoutine.division, // Solo, Duo, etc. - this is what we should group by!
              ageGroup: scheduleRoutine.ageGroup,
              droppedScores: flexibleScoreData.droppedScores,
              judgeScores: flexibleScoreData.judgeScores
            });
            
            allScoredRoutines.push({
              routineId: scheduleRoutine.routineNumber,
              routineName: displayTitle,
              performer: performerName,
              studio: getStudioName(scheduleRoutine),
              division: scheduleRoutine.category || 'Unknown', // This is performance division (Contemporary, Jazz, etc.)
              category: scheduleRoutine.division || 'Solo', // This is what we should group by (Solo, Duo, etc.)
              ageGroup: scheduleRoutine.ageGroup || 'Unknown',
              finalScore: flexibleScoreData.finalScore,
              award: flexibleScoreData.correlatedAward, // Use the correlated award (Platinum, Gold, etc.)
              rank: 0, // Will be set based on placement
              type: scheduleRoutine.type || 'Competitive',
              flexibleScoreData: flexibleScoreData,
              placement: '', // Will be calculated
              stageId: scheduleRoutine.stageId || extractStageIdFromCompetitionCode(competitionCode!),
              stageName: scheduleRoutine.stageName || getStageNameFromId(scheduleRoutine.stageId)
            });
          }
        });
        
        // Group routines by age division, then by category
        const groupedRoutines: { [ageGroup: string]: { [category: string]: AwardedRoutine[] } } = {};
        
        allScoredRoutines.forEach(routine => {
          if (!groupedRoutines[routine.ageGroup]) {
            groupedRoutines[routine.ageGroup] = {};
          }
          if (!groupedRoutines[routine.ageGroup][routine.category]) {
            groupedRoutines[routine.ageGroup][routine.category] = [];
          }
          groupedRoutines[routine.ageGroup][routine.category].push(routine);
        });
        
        // Process each age group and category
        const finalRoutines: AwardedRoutine[] = [];
        
        // Sort age groups by order (youngest to oldest)
        const sortedAgeGroups = Object.keys(groupedRoutines).sort((a, b) => 
          getAgeDivisionOrder(a, allScoredRoutines) - getAgeDivisionOrder(b, allScoredRoutines)
        );
        
        sortedAgeGroups.forEach(ageGroup => {
          // Sort categories by order (Solo, Duo/Trio, Small Group, etc.)
          const sortedCategories = Object.keys(groupedRoutines[ageGroup]).sort((a, b) => 
            getCategoryOrder(a) - getCategoryOrder(b)
          );
          
          sortedCategories.forEach(category => {
            const categoryRoutines = groupedRoutines[ageGroup][category];
            
            console.log(`Processing ${ageGroup} ${category} - ${categoryRoutines.length} routines:`, 
              categoryRoutines.map(r => ({ name: r.routineName, score: r.finalScore }))
            );
            
            // Sort by score descending (highest to lowest), with drop score tie-breaking
            categoryRoutines.sort((a, b) => {
              // First compare final scores
              if (a.finalScore !== b.finalScore) {
                return b.finalScore - a.finalScore;
              }
              
              // If final scores are tied, use drop scores for tie-breaking
              const aDropScore = a.flexibleScoreData?.droppedScores?.[0]?.totalScore || 
                                a.flexibleScoreData?.droppedScores?.[0]?.originalScore?.totalScore || 0;
              const bDropScore = b.flexibleScoreData?.droppedScores?.[0]?.totalScore || 
                                b.flexibleScoreData?.droppedScores?.[0]?.originalScore?.totalScore || 0;
              
              console.log(`Tie-breaking: ${a.routineName} (${a.finalScore}, drop: ${aDropScore}) vs ${b.routineName} (${b.finalScore}, drop: ${bDropScore})`);
              
              return bDropScore - aDropScore; // Higher drop score wins
            });
            
            console.log(`After sorting ${ageGroup} ${category}:`, 
              categoryRoutines.map(r => ({ name: r.routineName, score: r.finalScore }))
            );
            
            // Determine how many placements to include based on the variant
            let soloPlacements = 10; // Default to top 10 placements for solos
            let groupPlacements = 5; // Default to top 5 placements for groups
            
            // Parse variant from reportType (e.g., "highScoreByAge-20and10")
            if (reportType.includes('-')) {
              const variant = reportType.split('-')[1];
              if (variant === '10and5') {
                soloPlacements = 10;
                groupPlacements = 5;
              } else if (variant === '20and10') {
                soloPlacements = 20;
                groupPlacements = 10;
              }
            }
            
            const maxPlacements = isSoloCategory(category) ? soloPlacements : groupPlacements;
            
            // Filter by top N placements (not top N routines)
            const routinesWithPlacements = filterByTopPlacements(categoryRoutines, maxPlacements);
            
            // Add to final routines
            routinesWithPlacements.forEach(routine => {
              // Keep the original correlated award from flexible scoring (Platinum, Gold, etc.)
              // routine.award already contains the correlated award from flexibleScoreData
              console.log(`Assigned ${routine.placement} to ${routine.routineName} (score: ${routine.finalScore})`);
              finalRoutines.push(routine);
            });
          });
        });
        
        // Sort final routines for display (10th, 9th, 8th... to 1st)
        // Higher placement numbers first (10 > 9 > 8 > ... > 1) for MC reading order
        finalRoutines.sort((a, b) => {
          // First sort by age group order
          const ageOrderA = getAgeDivisionOrder(a.ageGroup, []);
          const ageOrderB = getAgeDivisionOrder(b.ageGroup, []);
          if (ageOrderA !== ageOrderB) return ageOrderA - ageOrderB;
          
          // Then by category order
          const categoryOrderA = getCategoryOrder(a.category);
          const categoryOrderB = getCategoryOrder(b.category);
          if (categoryOrderA !== categoryOrderB) return categoryOrderA - categoryOrderB;
          
          // Finally by placement for display (higher placement numbers first: 10th, 9th, 8th, etc.)
          // For MC reading: start with 10th place, end with 1st place
          return b.rank - a.rank; // Higher rank (10th) comes before lower rank (1st)
        });
        
        return finalRoutines;
      };
      
      // Helper function to get ordinal suffix (1st, 2nd, 3rd, etc.)
      const getOrdinalSuffix = (num: number): string => {
        const j = num % 10;
        const k = num % 100;
        if (j === 1 && k !== 11) return 'st';
        if (j === 2 && k !== 12) return 'nd';
        if (j === 3 && k !== 13) return 'rd';
        return 'th';
      };

      // Helper function to extract dropped score from routine data
      const getDroppedScore = (routine: AwardedRoutine): number => {
        // Check various possible locations for dropped score data
        const routineWithDropped = routine as any;
        
        if (routineWithDropped.droppedScores && routineWithDropped.droppedScores.length > 0) {
          return routineWithDropped.droppedScores[0].totalScore || routineWithDropped.droppedScores[0].originalScore?.totalScore || 0;
        }
        
        if (routine.flexibleScoreData?.droppedScores && routine.flexibleScoreData.droppedScores.length > 0) {
          return routine.flexibleScoreData.droppedScores[0].totalScore || routine.flexibleScoreData.droppedScores[0].originalScore?.totalScore || 0;
        }
        
        // If no dropped score found, return 0
        return 0;
      };

      // Helper function to calculate placements with proper tie handling and dropped score tie-breaker
      const calculatePlacementsWithTies = (routines: AwardedRoutine[], scoreField: string = 'finalScore'): AwardedRoutine[] => {
        if (!routines || routines.length === 0) {
          return routines;
        }

        // Sort routines by score with dropped score tie-breaker
        const sortedRoutines = [...routines].sort((a, b) => {
          const scoreA = (a as any)[scoreField] || 0;
          const scoreB = (b as any)[scoreField] || 0;
          
          // Primary sort: by final score (highest first)
          if (scoreA !== scoreB) {
            return scoreB - scoreA;
          }
          
          // Tie-breaker: use dropped score (highest dropped score wins)
          const aDropScore = getDroppedScore(a);
          const bDropScore = getDroppedScore(b);
          
          console.log(`Tie-breaker: ${a.routineName} (${scoreA}, drop: ${aDropScore}) vs ${b.routineName} (${scoreB}, drop: ${bDropScore})`);
          
          return bDropScore - aDropScore; // Higher dropped score wins the tie
        });

        let currentPlacement = 1;
        let previousScore: number | null = null;
        let previousDropScore: number | null = null;

        sortedRoutines.forEach((routine, index) => {
          const currentScore = (routine as any)[scoreField] || 0;
          const currentDropScore = getDroppedScore(routine);
          
          // For the first routine, always start at placement 1
          if (index === 0) {
            currentPlacement = 1;
          } else {
            // Check if this routine should have a different placement than the previous one
            const shouldAdvancePlacement = 
              currentScore !== previousScore || // Different main score
              (currentScore === previousScore && currentDropScore !== previousDropScore); // Same main score but different tie-breaker
            
            if (shouldAdvancePlacement) {
              currentPlacement = currentPlacement + 1;
            }
            // If neither condition is met, keep the same placement (true tie)
          }
          
          routine.rank = currentPlacement;
          routine.placement = `${currentPlacement}${getOrdinalSuffix(currentPlacement)}`;
          previousScore = currentScore;
          previousDropScore = currentDropScore;
          
          console.log(`Tie-aware placement: ${routine.routineName} - Score: ${currentScore}, Drop: ${currentDropScore}, Placement: ${routine.placement}`);
        });

        return sortedRoutines;
      };

      // Helper function to filter routines by top N placements (not top N routines)
      const filterByTopPlacements = (routines: AwardedRoutine[], maxPlacements: number): AwardedRoutine[] => {
        if (!routines || routines.length === 0) {
          return routines;
        }

        // First calculate placements for all routines
        const routinesWithPlacements = calculatePlacementsWithTies(routines, 'finalScore');
        
        // Filter to only include routines with placements <= maxPlacements
        const filteredRoutines = routinesWithPlacements.filter(routine => routine.rank <= maxPlacements);
        
        console.log(`Filtered to top ${maxPlacements} placements: ${filteredRoutines.length} routines (from ${routines.length} total)`);
        console.log(`Placement breakdown:`, filteredRoutines.reduce((acc, routine) => {
          acc[routine.rank] = (acc[routine.rank] || 0) + 1;
          return acc;
        }, {} as Record<number, number>));
        
        return filteredRoutines;
      };
      
      // Check if schedule data has duplicates
      const routineNumberCounts = new Map<string, number>();
      data.scheduleData.forEach((routine: any) => {
        const routineNum = routine.routineNumber || routine.id;
        routineNumberCounts.set(routineNum, (routineNumberCounts.get(routineNum) || 0) + 1);
      });
      
      // Find duplicates
      const duplicates = Array.from(routineNumberCounts.entries()).filter(([_, count]) => count > 1);
      if (duplicates.length > 0) {
        console.log('⚠️ Found duplicate routines in schedule data:', duplicates.slice(0, 10));
        console.log('⚠️ Total routines with duplicates:', duplicates.length);
      }
      
      // Deduplicate by routineNumber - keep the first occurrence
      const seenRoutineNumbers = new Set<string>();
      const deduplicatedScheduleData = data.scheduleData.filter((routine: any) => {
        const routineNum = routine.routineNumber || routine.id;
        if (seenRoutineNumbers.has(routineNum)) {
          return false; // Skip duplicate
        }
        seenRoutineNumbers.add(routineNum);
        return true;
      });
      
      console.log('🎯 DEBUG - Original schedule data count:', data.scheduleData.length);
      console.log('🎯 DEBUG - After deduplication:', deduplicatedScheduleData.length);
      console.log('🎯 DEBUG - Report type:', reportType);

      // Process data based on report type
      let processedRoutines: AwardedRoutine[] = [];
      
      if (reportType === 'highScoreByAge' || reportType.startsWith('highScoreByAge')) {
        // Process high score by age data
        processedRoutines = processHighScoreByAge(deduplicatedScheduleData, reportType);
      } else if (reportType === 'high-score-division' || reportType === 'highScoreByDivision' || reportType.startsWith('highScoreByDivision')) {
        // Process high score by performance division data
        processedRoutines = processHighScoreByPerformanceDivision(deduplicatedScheduleData, reportType);
      } else {
        // Default adjudication awards logic
        const awardedRoutines: AwardedRoutine[] = [];
      
      // Process each routine from deduplicated schedule data
      deduplicatedScheduleData.forEach((scheduleRoutine: any) => {
        // Use the internal routineId for consistent lookup (same as Performance Schedule fix)
        const routineId = scheduleRoutine.id || scheduleRoutine.routineNumber;
        const flexibleScoreData = getFlexibleScoreData(routineId);
        
        // For Greatest Hits and Studio Standouts, include all eligible routines (scored and unscored)
        // For other awards, only include routines that have been scored and have awards
        const isGreatestHitsOrStudioStandout = reportType === 'greatestHits' || reportType.startsWith('greatestHits') || 
                                               reportType === 'studioStandout' || reportType.startsWith('studioStandout');
        
        const shouldIncludeRoutine = isGreatestHitsOrStudioStandout || 
                                   (flexibleScoreData && flexibleScoreData.correlatedAward && flexibleScoreData.correlatedAward !== 'No Award');
        
        // Debug logging for routine 987
        if (routineId === '987') {
          console.log('🎯 DEBUG - Routine 987 processing:', {
            flexibleScoreData,
            isGreatestHitsOrStudioStandout,
            shouldIncludeRoutine,
            correlatedAward: flexibleScoreData?.correlatedAward
          });
        }
        
        if (shouldIncludeRoutine) {
          // Check if this routine is eligible for this report type
          if (!isRoutineEligibleForReport(scheduleRoutine, reportType)) {
            console.log(`Excluding routine ${scheduleRoutine.routineNumber} - not eligible for ${reportType}`);
            return; // Skip this routine
          }
          
          // Check if this routine should be excluded based on division settings
          if (shouldExcludeRoutine(scheduleRoutine, reportType)) {
            console.log(`Excluding routine ${scheduleRoutine.routineNumber} (${scheduleRoutine.division}) due to excludedDivisions setting`);
            return; // Skip this routine
          }
          
          // For Greatest Hits, check split age groups filtering
          // If an age group is split, only show routines matching the selected divisions for that age group
          if (isGreatestHitsOrStudioStandout && reportType.startsWith('greatestHits')) {
            const customAward = customAwards.find(award => {
              if (!award.enabled) return false;
              return reportType.startsWith(award.templateType);
            });
            
            if (customAward) {
              const routineAgeGroup = scheduleRoutine.ageGroup || 'Unknown';
              const routineDivision = scheduleRoutine.division || 'Unknown';
              const splitAgeGroups = customAward.config?.splitAgeGroups || {};
              
              // If this age group is split, check if the division matches either column
              if (routineAgeGroup in splitAgeGroups) {
                const splitColumns = splitAgeGroups[routineAgeGroup];
                // splitColumns is an array of two arrays: [column1Divisions, column2Divisions]
                // Check if division is in column 1 (index 0) or column 2 (index 1)
                const column1Divisions = splitColumns[0] || [];
                const column2Divisions = splitColumns[1] || [];
                const allAllowedDivisions = [...column1Divisions, ...column2Divisions];
                
                // If divisions are specified for this split age group, filter by them
                if (allAllowedDivisions.length > 0 && !allAllowedDivisions.includes(routineDivision)) {
                  console.log(`Excluding routine ${scheduleRoutine.routineNumber} - ${routineAgeGroup} is split and "${routineDivision}" is not in allowed divisions`);
                  return; // Skip this routine
                }
              }
              // If age group is not split, include all routines (no filtering needed)
            }
          }
          
          // For Studio Standouts, check if the studio meets eligibility criteria
          if (reportType === 'studioStandout' || reportType.startsWith('studioStandout')) {
            const customAward = customAwards.find(award => {
              if (!award.enabled) return false;
              return reportType.startsWith(award.templateType);
            });
            
            if (customAward && customAward.config?.eligibilityCriteria) {
              const studioName = getStudioName(scheduleRoutine);
              const isEligible = checkStudioEligibility(studioName, deduplicatedScheduleData, customAward.config.eligibilityCriteria);
              
              if (!isEligible) {
                console.log(`Excluding routine ${scheduleRoutine.routineNumber} from studio "${studioName}" - studio does not meet eligibility criteria`);
                return; // Skip this routine
              }
            }
          }
          
          const isSolo = scheduleRoutine.division === 'Solo';
          const routineTitle = scheduleRoutine.name || `Routine #${scheduleRoutine.routineNumber}`;
          const performerName = scheduleRoutine.performerName || '';
          
            // For solos, combine title and performer name (only if not already included)
            const displayTitle = isSolo && performerName && !routineTitle.includes(performerName)
            ? `${routineTitle} (${performerName})`
            : routineTitle;
          
          awardedRoutines.push({
            routineId: scheduleRoutine.routineNumber, // Use display number for UI
            routineName: displayTitle,
            performer: performerName,
            studio: getStudioName(scheduleRoutine), // Use studio name instead of code
            division: scheduleRoutine.division || 'Unknown',
            category: scheduleRoutine.category || 'Competitive',
            ageGroup: scheduleRoutine.ageGroup || 'Unknown',
            finalScore: flexibleScoreData ? flexibleScoreData.finalScore : 0,
            award: flexibleScoreData ? flexibleScoreData.correlatedAward : 'Not Scored',
            rank: 0, // Will be set after sorting
            type: scheduleRoutine.type || 'Competitive',
              flexibleScoreData: flexibleScoreData, // Include the full flexible score data (may be null)
              stageId: scheduleRoutine.stageId || extractStageIdFromCompetitionCode(competitionCode),
              stageName: scheduleRoutine.stageName || getStageNameFromId(scheduleRoutine.stageId)
          });
        }
      });
      
      // Sort by routine number (ascending order)
      awardedRoutines.sort((a, b) => parseInt(a.routineId) - parseInt(b.routineId));
      
      // Assign ranks based on sorted order
      awardedRoutines.forEach((routine, index) => {
        routine.rank = index + 1;
      });
      
        processedRoutines = awardedRoutines;
      }
      
      console.log('🎯 DEBUG - Processed routines count:', processedRoutines.length);
      console.log('🎯 DEBUG - First 3 routines:', processedRoutines.slice(0, 3));
      
      setRoutines(processedRoutines);
      setCompetitionName(data.competitionName || `Competition ${competitionCode}`);
      setLastUpdated(new Date());
      
      // Extract available stages from processed routines
      const stages = extractAvailableStages(processedRoutines);
      setAvailableStages(stages);
      
    } catch (error) {
      console.error('Error fetching awards data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // Only fetch awards data if awards configuration is loaded
    // Note: We don't wait for competitionCategories to load, but the eligibility check will handle it
    if (awards.length > 0) {
      fetchAwardsData();
    }
  }, [competitionCode, reportType, awards]);

  // Reset winners mode when report type changes
  useEffect(() => {
    setIsWinnersMode(false);
    setSelectedWinners([]);
    setEligibleRoutines([]);
  }, [reportType]);

  // Auto-load winners when in winners mode and dependencies change
  useEffect(() => {
    if (isWinnersMode && competitionCode && reportType) {
      console.log('Winners mode dependencies changed - reloading winners...');
      loadSelectedWinners();
    }
  }, [isWinnersMode, reportType, selectedStages]);

  const handlePrint = () => {
    window.print();
  };

  const handleRefresh = () => {
    fetchAwardsData();
  };

  // Helper function to escape CSV values
  const escapeCSVValue = (value: any): string => {
    if (value === null || value === undefined) return '';
    const stringValue = String(value);
    // If value contains comma, quote, or newline, wrap in quotes and escape quotes
    if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
      return `"${stringValue.replace(/"/g, '""')}"`;
    }
    return stringValue;
  };

  // CSV export function
  const exportToCSV = () => {
    // Determine which routines to export
    let routinesToExport: AwardedRoutine[];
    
    if (isWinnersMode && supportsWinners() && selectedWinners.length > 0) {
      // Export selected winners
      routinesToExport = selectedWinners;
    } else {
      // Export filtered routines
      routinesToExport = filterRoutinesByStages(routines);
    }

    if (routinesToExport.length === 0) {
      console.warn('No routines to export');
      return;
    }

    // Determine columns based on report type
    const hasPlacement = showsPlacementColumn(reportType);
    const hasStudioName = showsStudioNameColumn(reportType);
    const isAdjudication = reportType === 'adjudication';

    const headers: string[] = [];
    if (hasPlacement) headers.push('Placement');
    if (hasStudioName) {
      headers.push('Studio Name');
    } else {
      headers.push('#');
    }
    if (isAdjudication) headers.push('Studio');
    headers.push('Routine');
    headers.push(showScores ? 'Score' : 'Award');
    headers.push('Age Division');
    headers.push('Category');
    headers.push('Performance Division');

    // For grouped reports, don't add headers upfront - each section will have its own headers
    // For flat reports (adjudication), add headers at the beginning
    const isGroupedReport = reportType === 'highScoreByAge' || reportType.startsWith('highScoreByAge') ||
                           reportType === 'high-score-division' || reportType === 'highScoreByDivision' || reportType.startsWith('highScoreByDivision') ||
                           reportType === 'greatestHits' || reportType.startsWith('greatestHits') ||
                           reportType === 'studioStandout' || reportType.startsWith('studioStandout');
    
    const csvRows: string[] = isGroupedReport ? [] : [headers.map(escapeCSVValue).join(',')];

    // Helper function to get score display
    const getScoreDisplay = (routine: AwardedRoutine): string => {
      if (routine.award === 'Not Scored') {
        return 'Not Scored';
      }
      if (showScores) {
        const dropScores = routine.flexibleScoreData?.droppedScores || [];
        const droppedScore = dropScores.length > 0
          ? dropScores[0].totalScore || dropScores[0].originalScore?.totalScore || 0
          : 0;
        return `${routine.award} (${routine.finalScore.toFixed(0)}: ${droppedScore})`;
      }
      return routine.award;
    };

    // Helper function to create CSV row from routine
    const createCSVRow = (routine: AwardedRoutine): string => {
      const row: string[] = [];
      if (hasPlacement) {
        row.push(escapeCSVValue(routine.placement?.replace(/\D/g, '') || ''));
      }
      if (hasStudioName) {
        row.push(escapeCSVValue(routine.studio));
      } else {
        row.push(escapeCSVValue(routine.routineId));
      }
      if (isAdjudication) {
        row.push(escapeCSVValue(routine.studio));
      }
      row.push(escapeCSVValue(routine.routineName));
      row.push(escapeCSVValue(getScoreDisplay(routine)));
      row.push(escapeCSVValue(routine.ageGroup));
      row.push(escapeCSVValue(reportType === 'adjudication' ? routine.division : routine.category));
      row.push(escapeCSVValue(reportType === 'adjudication' ? routine.category : routine.division));
      return row.join(',');
    };

    // Group and export based on report type
    if (reportType === 'highScoreByAge' || reportType.startsWith('highScoreByAge')) {
      // Group by age group → category
      const groupedByAgeAndCategory: { [ageGroup: string]: { [category: string]: AwardedRoutine[] } } = {};
      routinesToExport.forEach(routine => {
        if (!groupedByAgeAndCategory[routine.ageGroup]) {
          groupedByAgeAndCategory[routine.ageGroup] = {};
        }
        if (!groupedByAgeAndCategory[routine.ageGroup][routine.category]) {
          groupedByAgeAndCategory[routine.ageGroup][routine.category] = [];
        }
        groupedByAgeAndCategory[routine.ageGroup][routine.category].push(routine);
      });

      const sortedAgeGroups = Object.keys(groupedByAgeAndCategory).sort((a, b) => 
        getAgeDivisionOrder(a) - getAgeDivisionOrder(b)
      );

      sortedAgeGroups.forEach(ageGroup => {
        const sortedCategories = Object.keys(groupedByAgeAndCategory[ageGroup]).sort((a, b) => 
          getCategoryOrder(a) - getCategoryOrder(b)
        );

        sortedCategories.forEach(category => {
          const categoryRoutines = groupedByAgeAndCategory[ageGroup][category];
          // Add section header
          csvRows.push('');
          csvRows.push(escapeCSVValue(`${ageGroup} ${category} High Score`));
          csvRows.push(headers.map(escapeCSVValue).join(','));
          
          // Add routines sorted by rank (10th place on top, 1st place on bottom - matching display order)
          const sortedRoutines = [...categoryRoutines].sort((a, b) => b.rank - a.rank);
          sortedRoutines.forEach(routine => {
            csvRows.push(createCSVRow(routine));
          });
        });
      });
    } else if (reportType === 'high-score-division' || reportType === 'highScoreByDivision' || reportType.startsWith('highScoreByDivision')) {
      // Group by age group → performance division
      const groupedByAgeAndDivision: { [ageGroup: string]: { [performanceDivision: string]: AwardedRoutine[] } } = {};
      routinesToExport.forEach(routine => {
        if (!groupedByAgeAndDivision[routine.ageGroup]) {
          groupedByAgeAndDivision[routine.ageGroup] = {};
        }
        if (!groupedByAgeAndDivision[routine.ageGroup][routine.division]) {
          groupedByAgeAndDivision[routine.ageGroup][routine.division] = [];
        }
        groupedByAgeAndDivision[routine.ageGroup][routine.division].push(routine);
      });

      const sortedAgeGroups = Object.keys(groupedByAgeAndDivision).sort((a, b) => 
        getAgeDivisionOrder(a) - getAgeDivisionOrder(b)
      );

      sortedAgeGroups.forEach(ageGroup => {
        const sortedPerformanceDivisions = Object.keys(groupedByAgeAndDivision[ageGroup]).sort();

        sortedPerformanceDivisions.forEach(performanceDivision => {
          const divisionRoutines = groupedByAgeAndDivision[ageGroup][performanceDivision];
          // Add section header
          csvRows.push('');
          csvRows.push(escapeCSVValue(`${ageGroup} ${performanceDivision} High Score`));
          csvRows.push(headers.map(escapeCSVValue).join(','));
          
          // Add routines sorted by rank (10th place on top, 1st place on bottom - matching display order)
          const sortedRoutines = [...divisionRoutines].sort((a, b) => b.rank - a.rank);
          sortedRoutines.forEach(routine => {
            csvRows.push(createCSVRow(routine));
          });
        });
      });
    } else if (reportType === 'greatestHits' || reportType.startsWith('greatestHits')) {
      // Group by age group
      const groupedByAge: { [ageGroup: string]: AwardedRoutine[] } = {};
      routinesToExport.forEach(routine => {
        if (!groupedByAge[routine.ageGroup]) {
          groupedByAge[routine.ageGroup] = [];
        }
        groupedByAge[routine.ageGroup].push(routine);
      });

      const customAward = customAwards.find(award => {
        if (!award.enabled) return false;
        return reportType.startsWith(award.templateType);
      });
      const awardDisplayName = customAward?.displayName || 'Award';
      const splitAgeGroups = customAward?.config?.splitAgeGroups || {};

      const sortedAgeGroups = Object.keys(groupedByAge).sort((a, b) => 
        getAgeDivisionOrder(a) - getAgeDivisionOrder(b)
      );

      sortedAgeGroups.forEach(ageGroup => {
        const ageRoutines = groupedByAge[ageGroup];
        const splitColumns = splitAgeGroups[ageGroup];
        
        // Check if this age group is split into two columns
        if (splitColumns && Array.isArray(splitColumns) && splitColumns.length >= 2) {
          const column1Divisions = splitColumns[0] || [];
          const column2Divisions = splitColumns[1] || [];
          
          // Filter routines for column 1
          const column1Routines = column1Divisions.length > 0
            ? ageRoutines.filter(routine => column1Divisions.includes(routine.division))
            : [];
          
          // Filter routines for column 2
          const column2Routines = column2Divisions.length > 0
            ? ageRoutines.filter(routine => column2Divisions.includes(routine.division))
            : [];
          
          // Column 1 section
          if (column1Routines.length > 0) {
            const column1Label = `${ageGroup} ${awardDisplayName} - ${getSplitColumnLabel(column1Divisions)}`;
            csvRows.push('');
            csvRows.push(escapeCSVValue(column1Label));
            csvRows.push(headers.map(escapeCSVValue).join(','));
            
            // Add routines sorted by score (highest to lowest)
            const sortedRoutines = [...column1Routines].sort((a, b) => {
              if (a.finalScore !== b.finalScore) {
                return b.finalScore - a.finalScore;
              }
              const aDropScore = a.flexibleScoreData?.droppedScores?.[0]?.totalScore || 
                                a.flexibleScoreData?.droppedScores?.[0]?.originalScore?.totalScore || 0;
              const bDropScore = b.flexibleScoreData?.droppedScores?.[0]?.totalScore || 
                                b.flexibleScoreData?.droppedScores?.[0]?.originalScore?.totalScore || 0;
              return bDropScore - aDropScore;
            });
            
            sortedRoutines.forEach(routine => {
              csvRows.push(createCSVRow(routine));
            });
          }
          
          // Column 2 section
          if (column2Routines.length > 0) {
            const column2Label = `${ageGroup} ${awardDisplayName} - ${getSplitColumnLabel(column2Divisions)}`;
            csvRows.push('');
            csvRows.push(escapeCSVValue(column2Label));
            csvRows.push(headers.map(escapeCSVValue).join(','));
            
            // Add routines sorted by score (highest to lowest)
            const sortedRoutines = [...column2Routines].sort((a, b) => {
              if (a.finalScore !== b.finalScore) {
                return b.finalScore - a.finalScore;
              }
              const aDropScore = a.flexibleScoreData?.droppedScores?.[0]?.totalScore || 
                                a.flexibleScoreData?.droppedScores?.[0]?.originalScore?.totalScore || 0;
              const bDropScore = b.flexibleScoreData?.droppedScores?.[0]?.totalScore || 
                                b.flexibleScoreData?.droppedScores?.[0]?.originalScore?.totalScore || 0;
              return bDropScore - aDropScore;
            });
            
            sortedRoutines.forEach(routine => {
              csvRows.push(createCSVRow(routine));
            });
          }
        } else {
          // Not split - show all routines for this age group
          csvRows.push('');
          csvRows.push(escapeCSVValue(`${ageGroup} ${awardDisplayName}`));
          csvRows.push(headers.map(escapeCSVValue).join(','));
          
          // Add routines sorted by score (highest to lowest)
          const sortedRoutines = [...ageRoutines].sort((a, b) => {
            if (a.finalScore !== b.finalScore) {
              return b.finalScore - a.finalScore;
            }
            const aDropScore = a.flexibleScoreData?.droppedScores?.[0]?.totalScore || 
                              a.flexibleScoreData?.droppedScores?.[0]?.originalScore?.totalScore || 0;
            const bDropScore = b.flexibleScoreData?.droppedScores?.[0]?.totalScore || 
                              b.flexibleScoreData?.droppedScores?.[0]?.originalScore?.totalScore || 0;
            return bDropScore - aDropScore;
          });
          
          sortedRoutines.forEach(routine => {
            csvRows.push(createCSVRow(routine));
          });
        }
      });
    } else if (reportType === 'studioStandout' || reportType.startsWith('studioStandout')) {
      // Single flat list
      const customAward = customAwards.find(award => {
        if (!award.enabled) return false;
        return reportType.startsWith(award.templateType);
      });
      const awardDisplayName = customAward?.displayName || 'Studio Standouts';
      
      // Add section header
      csvRows.push('');
      csvRows.push(escapeCSVValue(awardDisplayName));
      csvRows.push(headers.map(escapeCSVValue).join(','));
      
      // Sort by studio name (alphabetically), then by score (highest to lowest)
      const sortedRoutines = [...routinesToExport].sort((a, b) => {
        // First compare studio names alphabetically
        const studioA = (a.studio || '').toLowerCase();
        const studioB = (b.studio || '').toLowerCase();
        if (studioA !== studioB) {
          return studioA.localeCompare(studioB);
        }
        
        // If same studio, compare final scores (highest to lowest)
        if (a.finalScore !== b.finalScore) {
          return b.finalScore - a.finalScore;
        }
        
        // If final scores are tied, use drop scores for tie-breaking
        const aDropScore = a.flexibleScoreData?.droppedScores?.[0]?.totalScore || 
                          a.flexibleScoreData?.droppedScores?.[0]?.originalScore?.totalScore || 0;
        const bDropScore = b.flexibleScoreData?.droppedScores?.[0]?.totalScore || 
                          b.flexibleScoreData?.droppedScores?.[0]?.originalScore?.totalScore || 0;
        return bDropScore - aDropScore;
      });
      
      sortedRoutines.forEach(routine => {
        csvRows.push(createCSVRow(routine));
      });
    } else {
      // Adjudication - single flat list (header already added at the beginning)
      routinesToExport.forEach(routine => {
        csvRows.push(createCSVRow(routine));
      });
    }

    // Create CSV content
    const csvContent = csvRows.join('\n');

    // Create and download the CSV file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
    const filename = `${competitionCode || 'awards'}_${reportType}_${timestamp}.csv`;
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Remove toggleDarkMode as we now use the theme context

  const toggleShowScores = () => {
    setShowScores(!showScores);
  };

  // Helper function to get age division order based on min age
  const getAgeDivisionOrder = (ageGroup: string): number => {
    // For now, we'll use a basic ordering. In the future, this could fetch from HitchKick API
    // to get the actual minAge/maxAge values
    const ageOrderMap: { [key: string]: number } = {
      'PeeWee': 1,
      'Mini': 2,
      'Junior': 3,
      'Teen': 4,
      'Senior': 5,
      'Open': 6,
      'Streetz Stars': 1, // Based on your sample data
    };
    
    return ageOrderMap[ageGroup] || 999; // Unknown age groups go last
  };

  // Helper function to get template metadata for current report type
  const getTemplateMetadata = (type: ReportType) => {
    // First try to find from custom awards
    const customAward = customAwards.find(award => {
      if (!award.enabled) return false;
      return type.startsWith(award.templateType);
    });
    
    if (customAward) {
      const template = getTemplateById(customAward.templateType);
      return template;
    }
    
    // Fallback to direct template lookup for built-in types
    return getTemplateById(type);
  };

  // Helper function to determine if report type uses placement-based alternating row colors
  const usesPlacementBasedColors = (type: ReportType): boolean => {
    const template = getTemplateMetadata(type);
    if (template?.usesPlacementBasedColors !== undefined) {
      return template.usesPlacementBasedColors;
    }
    
    // Fallback for legacy types
    return type === 'high-score-age' || 
           type === 'high-score-division' ||
           type === 'highScoreByAge' ||
           type === 'highScoreByDivision' ||
           type.startsWith('highScoreByAge') ||
           type.startsWith('highScoreByDivision');
  };

  // Helper function to get the display name for the current report type
  const getReportDisplayName = (): string => {
    // Find the matching report in availableReports
    const report = availableReports.find(r => r.value === reportType);
    if (report) {
      return report.label;
    }
    
    // Fallback for built-in report types
    switch (reportType) {
      case 'adjudication':
        return 'Adjudication Awards';
      case 'highScoreByAge':
      case 'high-score-age':
        return 'High Score by Age';
      case 'highScoreByDivision':
      case 'high-score-division':
        return 'High Score by Performance Division';
      default:
        return 'Awards Report';
    }
  };

  // Helper function to determine if report type shows placement column
  const showsPlacementColumn = (type: ReportType): boolean => {
    const template = getTemplateMetadata(type);
    if (template?.showsPlacement !== undefined) {
      return template.showsPlacement;
    }
    
    // Fallback for legacy types
    return type === 'high-score-age' || 
           type === 'high-score-division' ||
           type === 'highScoreByAge' ||
           type === 'highScoreByDivision' ||
           type.startsWith('highScoreByAge') ||
           type.startsWith('highScoreByDivision');
  };

  // Helper function to determine if report shows studio name in first column
  const showsStudioNameColumn = (type: ReportType): boolean => {
    const template = getTemplateMetadata(type);
    if (template?.showsStudioName !== undefined) {
      return template.showsStudioName;
    }
    
    // Fallback for legacy types
    return type === 'high-score-age' || 
           type === 'high-score-division' ||
           type === 'greatest-hits' ||
           type === 'greatest-hits-winners' ||
           type === 'studio-standout' ||
           type === 'studio-standout-winners' ||
           type === 'highScoreByAge' ||
           type === 'highScoreByDivision' ||
           type.startsWith('highScoreByAge') ||
           type.startsWith('highScoreByDivision');
  };

  // Authentication check - show loading or redirect if not authenticated
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return null; // Will redirect via useEffect
  }

  return (
    <div className="relative">
      {/* Stage Background and Particles */}
      <StageBackground />
      <DanceParticles />

      {/* Save Notification */}
      {saveNotification.show && (
        <div className="fixed top-4 right-4 z-50">
          <motion.div
            initial={{ opacity: 0, x: 100, scale: 0.9 }}
            animate={{ opacity: 1, x: 0, scale: 1 }}
            exit={{ opacity: 0, x: 100, scale: 0.9 }}
            className={`px-6 py-1.5 print:py-4 rounded-xl shadow-2xl backdrop-blur-xl border max-w-sm ${
              saveNotification.type === 'success'
                ? 'bg-green-500/20 border-green-400/50 text-green-100'
                : 'bg-red-500/20 border-red-400/50 text-red-100'
            }`}
          >
            <div className="flex items-center gap-3">
              <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                saveNotification.type === 'success' ? 'bg-green-500' : 'bg-red-500'
              }`}>
                {saveNotification.type === 'success' ? (
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                ) : (
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                )}
              </div>
              <div>
                <p className="font-semibold text-sm">{saveNotification.message}</p>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Password Modal Overlay */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center px-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            className="bg-white/10 backdrop-blur-xl rounded-2xl border border-white/20 p-8 max-w-md w-full"
          >
            <div className="text-center mb-6">
              <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
                <Lock className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-2">Awards Access</h3>
              <p className="text-white/70">Enter the password to view awards</p>
            </div>
            
            <div className="space-y-4">
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handlePasswordSubmit()}
                  placeholder="Enter password"
                  className="w-full px-4 py-1.5 print:py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:border-blue-400/50 focus:bg-white/15 transition-all pr-12"
                  autoFocus
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-white/50 hover:text-white/80 transition-colors"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              
              {passwordError && (
                <motion.p
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-red-400 text-sm text-center"
                >
                  {passwordError}
                </motion.p>
              )}
              
              <button
                onClick={handlePasswordSubmit}
                className="w-full px-4 py-1.5 print:py-3 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-xl hover:shadow-lg hover:shadow-blue-500/25 transition-all font-semibold"
              >
                Access Awards
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center px-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            className="bg-white/10 backdrop-blur-xl rounded-2xl border border-white/20 p-8 max-w-md w-full"
          >
            <div className="text-center mb-6">
              <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-red-500 to-rose-600 flex items-center justify-center">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-white mb-2">Clear All Winners?</h3>
              <p className="text-white/70 mb-1">
                Are you sure you want to clear all selected winners for this <span className="font-semibold text-white">{reportType}</span> report?
              </p>
              <p className="text-red-300 text-sm font-medium">
                This action cannot be undone.
              </p>
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowConfirmModal(false);
                  setConfirmAction(null);
                }}
                className="flex-1 px-4 py-1.5 print:py-3 bg-white/10 border border-white/20 rounded-xl text-white hover:bg-white/15 transition-all font-semibold"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (confirmAction) {
                    confirmAction();
                  }
                }}
                className="flex-1 px-4 py-1.5 print:py-3 bg-gradient-to-r from-red-500 to-rose-600 text-white rounded-xl hover:shadow-lg hover:shadow-red-500/25 transition-all font-semibold"
              >
                Clear All Winners
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Main Content - Only show when authorized */}
      {isAuthorized && (
      
      <div className="fixed inset-0 flex flex-col overflow-auto bg-black text-white no-overscroll print:static print:bg-white print:text-black print:overflow-visible print:block print:h-auto print:inset-auto">
        <div className="w-full flex flex-col flex-grow relative z-20 max-w-7xl mx-auto px-2 print:max-w-none print:px-0 print:relative print:z-auto">
        {/* Header - hidden when printing */}
        <div className="print:hidden flex-shrink-0 pt-8 pb-6 relative z-20">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="relative rounded-2xl overflow-hidden bg-white/5 backdrop-blur-xl border border-white/20 shadow-2xl p-6"
          >
            <div className="flex justify-between items-center">
              {/* Left Section - Title and Info */}
              <div className="flex-grow min-w-0">
                <motion.h1
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.4, delay: 0.1 }}
                  className="text-2xl font-black leading-none"
                >
                  <span className="text-white">AWARDS </span>
                  <span className="bg-gradient-to-r from-pink-400 via-purple-400 to-orange-400 bg-clip-text text-transparent">VISUALIZER</span>
                </motion.h1>
                
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.4, delay: 0.2 }}
                  className="flex items-center gap-1.5 mt-0.5"
                >
                  {competitionName && competitionName !== competitionCode ? (
                    <>
                      <span className="text-sm truncate flex-shrink min-w-0 text-white/70" title={competitionName}>
                        {competitionName}
                      </span>
                    </>
                  ) : competitionCode ? (
                    <>
                      <span className="text-sm truncate flex-shrink min-w-0 text-white/70" title={`Competition: ${competitionCode}`}>
                        {competitionCode}
                      </span>
                    </>
                  ) : null}
                  
                  {lastUpdated && (
                    <>
                      <span className="flex-shrink-0 font-bold">•</span>
                      <span className="text-xs text-white/50" title={`Last updated: ${lastUpdated.toLocaleString()}`}>
                        Updated {lastUpdated.toLocaleTimeString()}
                      </span>
                    </>
                  )}
                </motion.div>
              </div>

              {/* Right Section - Controls */}
              <div className="flex items-center gap-2 flex-shrink-0">
                {/* Report Type Dropdown - Fixed width with truncation */}
                <div className="relative">
                  <select
                    value={reportType}
                    onChange={(e) => setReportType(e.target.value as ReportType)}
                    className="w-44 px-3 py-2 rounded-full bg-gradient-to-r from-gray-800/80 to-gray-900/80 border border-gray-600/60 text-white hover:from-gray-700/90 hover:to-gray-800/90 transition-all duration-300 backdrop-blur-xl text-sm font-medium shadow-lg truncate appearance-none cursor-pointer"
                    title={availableReports.find(r => r.value === reportType)?.label || ''}
                  >
                    {availableReports.map(report => (
                      <option 
                        key={report.value} 
                        value={report.value} 
                        className="bg-gray-800 text-white"
                        style={{ backgroundColor: '#1f2937', color: '#ffffff' }}
                      >
                        {report.label}
                      </option>
                    ))}
                  </select>
                  {/* Custom dropdown arrow */}
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                    <svg className="w-4 h-4 text-white/60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>

                {/* Stage Filter Dropdown - Fixed width with truncation */}
                {availableStages.length > 0 && (
                  <div className="relative">
                    <select
                      value={selectedStages.includes('all') ? 'all' : selectedStages[0] || 'all'}
                      onChange={(e) => handleStageFilterChange(e.target.value)}
                      className="w-32 px-3 py-2 rounded-full bg-gradient-to-r from-purple-800/80 to-indigo-900/80 border border-purple-600/60 text-white hover:from-purple-700/90 hover:to-indigo-800/90 transition-all duration-300 backdrop-blur-xl text-sm font-medium shadow-lg truncate appearance-none cursor-pointer"
                      title={selectedStages.includes('all') ? 'All Stages' : availableStages.find(s => s.id === selectedStages[0])?.name || ''}
                    >
                      <option 
                        value="all" 
                        className="bg-purple-800 text-white"
                        style={{ backgroundColor: '#6b21a8', color: '#ffffff' }}
                      >
                        All Stages
                      </option>
                      {availableStages.map(stage => (
                        <option 
                          key={stage.id} 
                          value={stage.id} 
                          className="bg-purple-800 text-white"
                          style={{ backgroundColor: '#6b21a8', color: '#ffffff' }}
                        >
                          {stage.name}
                        </option>
                      ))}
                    </select>
                    {/* Custom dropdown arrow */}
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                      <svg className="w-4 h-4 text-white/60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </div>
                )}

                {/* Winners Mode Toggle - Compact design */}
                {supportsWinners() && (
                  <button
                    onClick={toggleWinnersMode}
                    className={`flex items-center gap-1.5 px-3 py-2 rounded-full transition-all duration-300 backdrop-blur-xl text-sm font-medium whitespace-nowrap ${
                      isWinnersMode
                        ? 'bg-gradient-to-r from-amber-500/30 to-yellow-500/30 border border-amber-400/50 text-amber-200 hover:from-amber-500/40 hover:to-yellow-500/40'
                        : 'bg-gradient-to-r from-gray-500/30 to-gray-600/30 border border-gray-400/50 text-gray-200 hover:from-gray-500/40 hover:to-gray-600/40'
                    }`}
                    title={isWinnersMode ? 'Exit Winners Mode' : 'Select Winners Mode'}
                  >
                    <Trophy className="w-4 h-4 flex-shrink-0" />
                    <span className="hidden sm:inline">{isWinnersMode ? 'Exit Winners' : 'Select Winners'}</span>
                    <span className="sm:hidden">{isWinnersMode ? 'Exit' : 'Select'}</span>
                  </button>
                )}
                
                {/* Show/Hide Scores Toggle - Icon only */}
                <button
                  onClick={toggleShowScores}
                  className={`flex items-center justify-center px-3 py-2 rounded-full transition-all duration-300 backdrop-blur-xl text-sm font-medium whitespace-nowrap ${
                    showScores
                      ? 'bg-gradient-to-r from-green-500/30 to-emerald-500/30 border border-green-400/50 text-green-200 hover:from-green-500/40 hover:to-emerald-500/40'
                      : 'bg-gradient-to-r from-gray-500/30 to-gray-600/30 border border-gray-400/50 text-gray-200 hover:from-gray-500/40 hover:to-gray-600/40'
                  }`}
                  title={showScores ? 'Hide Scores' : 'Show Scores'}
                >
                  {showScores ? (
                    <Eye className="w-4 h-4 flex-shrink-0" />
                  ) : (
                    <EyeOff className="w-4 h-4 flex-shrink-0" />
                  )}
                </button>
                
                {/* Refresh Button - Icon only */}
                <button
                  onClick={handleRefresh}
                  disabled={isLoading}
                  className="flex items-center justify-center px-3 py-2 rounded-full bg-gradient-to-r from-blue-500/30 to-indigo-500/30 border border-blue-400/50 text-blue-200 hover:from-blue-500/40 hover:to-indigo-500/40 transition-all duration-300 backdrop-blur-xl text-sm font-medium disabled:opacity-50 whitespace-nowrap"
                  title="Refresh Data"
                >
                  {isLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin flex-shrink-0" />
                  ) : (
                    <RefreshCcw className="w-4 h-4 flex-shrink-0" />
                  )}
                </button>
                
                {/* Print Button - Icon only on smaller screens */}
                <button
                  onClick={handlePrint}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-full bg-gradient-to-r from-green-500/30 to-emerald-500/30 border border-green-400/50 text-green-200 hover:from-green-500/40 hover:to-emerald-500/40 transition-all duration-300 backdrop-blur-xl text-sm font-medium whitespace-nowrap"
                  title="Print Report"
                >
                  <Printer className="w-4 h-4 flex-shrink-0" />
                  <span className="hidden xl:inline">Print</span>
                </button>
                
                {/* CSV Download Button - Icon only on smaller screens */}
                <button
                  onClick={exportToCSV}
                  disabled={isLoading || filterRoutinesByStages(routines).length === 0}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-full bg-gradient-to-r from-blue-500/30 to-indigo-500/30 border border-blue-400/50 text-blue-200 hover:from-blue-500/40 hover:to-indigo-500/40 transition-all duration-300 backdrop-blur-xl text-sm font-medium whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Download CSV"
                >
                  <Download className="w-4 h-4 flex-shrink-0" />
                  <span className="hidden xl:inline">Download CSV</span>
                </button>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Main Content */}
        <div className="flex-grow pb-8 relative z-20 print:px-0 print:py-0 print:max-w-none">
          {/* Print Layout - New System */}
          <div className="hidden print:block print-container-fix">
            {(() => {
              // Determine which routines to print based on mode
              let routinesToPrint: AwardedRoutine[];
              let reportTitle = getReportDisplayName();
              
              if (isWinnersMode && supportsWinners() && selectedWinners.length > 0) {
                // In winners mode with selected winners - print only the selected winners
                routinesToPrint = selectedWinners;
                
                // Get the display name from custom awards for winners
                const customAward = customAwards.find(award => {
                  if (!award.enabled) return false;
                  return reportType.startsWith(award.templateType);
                });
                const awardDisplayName = customAward?.displayName || 'Award';
                reportTitle = `${awardDisplayName} Winners`;
              } else {
                // Normal mode - print all filtered routines
                routinesToPrint = filterRoutinesByStages(routines);
              }
              
              const printSections = PrintLayoutFactory.createLayout(reportType, routinesToPrint, customAwards, isWinnersMode && supportsWinners() && selectedWinners.length > 0);
              const stageFilterText = !selectedStages.includes('all') && availableStages.length > 0
                ? (selectedStages.length === 1 
                    ? availableStages.find(s => s.id === selectedStages[0])?.name || 'Stage Filter'
                    : `${selectedStages.length} Stages`)
                : undefined;

              return (
                <PrintTemplate
                  sections={printSections}
                  reportTitle={reportTitle}
                  competitionName={competitionName}
                  showScores={showScores}
                  generatedAt={new Date()}
                  stageFilter={stageFilterText}
                />
              );
            })()}
          </div>

          {isLoading ? (
            <div className="flex justify-center items-center h-full">
              <motion.div 
                className="text-center"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3 }}
              >
                <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-purple-500/20 to-pink-500/20 rounded-2xl flex items-center justify-center border border-purple-400/40">
                  <Loader2 className="w-8 h-8 animate-spin text-purple-400" />
                </div>
                <h3 className="text-xl font-black text-white mb-2">LOADING AWARDS</h3>
                <p className="text-white/70">Fetching competition results...</p>
              </motion.div>
            </div>
          ) : filterRoutinesByStages(routines).length === 0 ? (
            <div className="flex justify-center items-center h-full">
              <motion.div 
                className="text-center"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3 }}
              >
                <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-yellow-500/20 to-orange-500/20 rounded-2xl flex items-center justify-center border border-yellow-400/40">
                  <Trophy className="w-8 h-8 text-yellow-400" />
                </div>
                <h3 className="text-xl font-black text-white mb-2">NO AWARDS YET</h3>
                <p className="text-white/70">Awards will appear here as routines are scored and processed.</p>
              </motion.div>
            </div>
          ) : (
            <>
              {(() => {
                // Apply stage filtering to routines
                const filteredRoutines = filterRoutinesByStages(routines);
                
                // Check if we're in winners mode
                if (isWinnersMode && supportsWinners()) {
                  return (
                    <>
                      {/* Winners Mode Layout */}
                      <div className="space-y-6">
                        {/* Winner Management Controls */}
                        <StageCard className="min-h-0 p-4 print:hidden" glowColor="orange">
                          <div className="flex flex-wrap items-center justify-between gap-4">
                            <div className="flex items-center gap-2">
                              <Trophy className="w-5 h-5 text-amber-400" />
                              <h3 className="text-lg font-bold text-white">Winner Selection</h3>
                              <span className="text-xs px-2.5 py-1 rounded-full bg-gradient-to-r from-amber-500/20 to-yellow-500/20 border border-amber-400/40 text-amber-300 backdrop-blur-xl">
                                <span className="font-bold">{selectedWinners.length}</span> Selected
                              </span>
                              {selectedWinners.length > 0 && (
                                <span className="text-xs text-amber-300/70">
                                  • Auto-loaded from saved data
                                </span>
                              )}
                            </div>
                            
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => saveSelectedWinners()}
                                className="flex items-center gap-1.5 px-3 py-2 rounded-full bg-gradient-to-r from-green-500/30 to-emerald-500/30 border border-green-400/50 text-green-200 hover:from-green-500/40 hover:to-emerald-500/40 transition-all duration-300 backdrop-blur-xl text-sm font-medium whitespace-nowrap"
                                title="Save Selected Winners"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3-3m0 0l-3 3m3-3v12" />
                                </svg>
                                <span className="hidden sm:inline">Save Winners</span>
                              </button>
                              
                              <button
                                onClick={deleteSelectedWinners}
                                className="flex items-center gap-1.5 px-3 py-2 rounded-full bg-gradient-to-r from-red-500/30 to-rose-500/30 border border-red-400/50 text-red-200 hover:from-red-500/40 hover:to-rose-500/40 transition-all duration-300 backdrop-blur-xl text-sm font-medium whitespace-nowrap"
                                title="Clear All Winners"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                                <span className="hidden sm:inline">Clear All</span>
                              </button>
                            </div>
                          </div>
                        </StageCard>
                        {(() => {
                          // For Greatest Hits, organize by age groups with individual winner sections; for Studio Standouts, show traditional layout
                          if (reportType === 'greatestHits' || reportType.startsWith('greatestHits')) {
                            // Group routines by age division for Greatest Hits
                            const groupedByAge: { [ageGroup: string]: AwardedRoutine[] } = {};
                            filteredRoutines.forEach(routine => {
                              if (!groupedByAge[routine.ageGroup]) {
                                groupedByAge[routine.ageGroup] = [];
                              }
                              groupedByAge[routine.ageGroup].push(routine);
                            });
                            
                            // Sort age groups by order (youngest to oldest)
                            const sortedAgeGroups = Object.keys(groupedByAge).sort((a, b) => 
                              getAgeDivisionOrder(a) - getAgeDivisionOrder(b)
                            );

                            // Get the display name and split configuration from custom awards
                            const customAward = customAwards.find(award => {
                              if (!award.enabled) return false;
                              return reportType.startsWith(award.templateType);
                            });
                            const awardDisplayName = customAward?.displayName || 'Award';
                            const splitAgeGroups = customAward?.config?.splitAgeGroups || {};

                            // Helper function to render eligible routines and winners tables
                            const renderWinnersSection = (routines: AwardedRoutine[], headerText: string, winners: AwardedRoutine[], key: string) => {
                              if (routines.length === 0 && winners.length === 0) return null;
                              
                              // Sort routines by score
                              const sortedRoutines = [...routines].sort((a, b) => {
                                if (a.finalScore !== b.finalScore) {
                                  return b.finalScore - a.finalScore;
                                }
                                const aDropScore = a.flexibleScoreData?.droppedScores?.[0]?.totalScore || 
                                                  a.flexibleScoreData?.droppedScores?.[0]?.originalScore?.totalScore || 0;
                                const bDropScore = b.flexibleScoreData?.droppedScores?.[0]?.totalScore || 
                                                  b.flexibleScoreData?.droppedScores?.[0]?.originalScore?.totalScore || 0;
                                return bDropScore - aDropScore;
                              });

                              return (
                                <div key={key} className="space-y-4">
                                  {/* Eligible Routines */}
                                  <StageCard className="min-h-0 p-6 print:hidden" glowColor="blue">
                                    <div className="flex justify-between items-center mb-6">
                                      <h3 className="text-xl font-black text-white">{headerText} - ELIGIBLE ROUTINES</h3>
                                      <span className="text-xs px-2.5 py-1 rounded-full bg-gradient-to-r from-blue-500/20 to-indigo-500/20 border border-blue-400/40 text-blue-300 backdrop-blur-xl">
                                        <span className="font-bold">{sortedRoutines.length}</span> Eligible
                                      </span>
                                    </div>
                                    
                                    {sortedRoutines.length === 0 ? (
                                      <div className="text-center py-8">
                                        <p className="text-white/50">No eligible routines for this section.</p>
                                      </div>
                                    ) : (
                                      <div className="overflow-x-auto">
                                        <table className="min-w-full text-sm table-fixed">
                                          <colgroup>
                                            <col className="w-44" />
                                            <col className="w-56" />
                                            <col className="w-48" />
                                            <col className="w-24" />
                                            <col className="w-24" />
                                            <col className="w-32" />
                                            <col className="w-16" />
                                          </colgroup>
                                          <thead>
                                            <tr className="border-b border-white/20">
                                              <th className="px-4 py-1.5 print:py-3 text-left text-xs font-semibold text-white/80 uppercase tracking-wider">Studio Name</th>
                                              <th className="px-4 py-1.5 print:py-3 text-left text-xs font-semibold text-white/80 uppercase tracking-wider">Routine</th>
                                              <th className="px-4 py-1.5 print:py-3 text-left text-xs font-semibold text-white/80 uppercase tracking-wider">Score</th>
                                              <th className="px-4 py-1.5 print:py-3 text-left text-xs font-semibold text-white/80 uppercase tracking-wider">Age Division</th>
                                              <th className="px-4 py-1.5 print:py-3 text-left text-xs font-semibold text-white/80 uppercase tracking-wider">Category</th>
                                              <th className="px-4 py-1.5 print:py-3 text-left text-xs font-semibold text-white/80 uppercase tracking-wider">Performance Division</th>
                                              <th className="px-2 py-1.5 print:py-3 text-left text-xs font-semibold text-white/80 uppercase tracking-wider">Add</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {sortedRoutines.map((routine) => {
                                              const isAlreadySelected = selectedWinners.find(w => w.routineId === routine.routineId);
                                              return (
                                                <tr key={routine.routineId} className={`border-b border-white/10 ${isAlreadySelected ? 'opacity-50' : 'hover:bg-white/5'} transition-colors`}>
                                                  <td className="px-4 py-1.5 print:py-4 text-sm font-bold text-white break-words">{routine.studio}</td>
                                                  <td className="px-4 py-1.5 print:py-4 text-sm text-white/90 break-words">
                                                    <div className="font-medium">{routine.routineName}</div>
                                                  </td>
                                                  <td className="px-4 py-1.5 print:py-4 whitespace-nowrap text-sm text-left score-cell font-bold text-white/80">
                                                    {routine.award === 'Not Scored' ? (
                                                      <span className="font-bold text-gray-400">Not Scored</span>
                                                    ) : showScores ? (
                                                      (() => {
                                                        const dropScoresText = routine.flexibleScoreData?.droppedScores && routine.flexibleScoreData.droppedScores.length > 0
                                                          ? `: ${routine.flexibleScoreData.droppedScores.map((d: any) => d.totalScore || d.originalScore?.totalScore || 0).join(', ')}`
                                                          : '';
                                                        return (
                                                          <span className="font-bold">
                                                            {routine.award} ({routine.finalScore.toFixed(0)}{dropScoresText})
                                                          </span>
                                                        );
                                                      })()
                                                    ) : (
                                                      <span className="font-bold">{routine.award}</span>
                                                    )}
                                                  </td>
                                                  <td className="px-4 py-1.5 print:py-4 whitespace-nowrap text-sm text-white/80">{routine.ageGroup}</td>
                                                  <td className="px-4 py-1.5 print:py-4 whitespace-nowrap text-sm text-white/80">{routine.category}</td>
                                                  <td className="px-4 py-1.5 print:py-4 whitespace-nowrap text-sm text-white/80">{routine.division}</td>
                                                  <td className="px-2 py-1.5 print:py-3 text-left">
                                                    {isAlreadySelected ? (
                                                      <span className="text-green-400 text-xs">Added</span>
                                                    ) : (
                                                      <button
                                                        onClick={() => addToWinners(routine)}
                                                        className="w-8 h-8 rounded-full bg-gradient-to-r from-green-500/30 to-emerald-500/30 border border-green-400/50 text-green-200 hover:from-green-500/40 hover:to-emerald-500/40 transition-all duration-200 flex items-center justify-center"
                                                      >
                                                        <span className="text-lg font-bold">+</span>
                                                      </button>
                                                    )}
                                                  </td>
                                                </tr>
                                              );
                                            })}
                                          </tbody>
                                        </table>
                                      </div>
                                    )}
                                  </StageCard>

                                  {/* Selected Winners */}
                                  <StageCard className="min-h-0 p-6 print:hidden" glowColor="pink">
                                    <div className="flex justify-between items-center mb-6">
                                      <h3 className="text-xl font-black bg-gradient-to-r from-pink-400 via-rose-400 to-pink-500 bg-clip-text text-transparent">{headerText} - WINNERS</h3>
                                      <span className="text-xs px-2.5 py-1 rounded-full bg-gradient-to-r from-pink-500/20 to-rose-500/20 border border-pink-400/40 text-pink-300 backdrop-blur-xl">
                                        <span className="font-bold">{winners.length}</span> Selected
                                      </span>
                                    </div>
                                    
                                    {winners.length === 0 ? (
                                      <div className="text-center py-12">
                                        <Trophy className="w-16 h-16 mx-auto mb-4 text-pink-400/50" />
                                        <p className="text-white/50">No winners selected yet for this section. Use the + buttons above to add routines.</p>
                                      </div>
                                    ) : (
                                      <div className="overflow-x-auto">
                                        <table className="min-w-full text-sm table-fixed">
                                          <colgroup>
                                            <col className="w-48" />
                                            <col className="w-64" />
                                            <col className="w-32" />
                                            <col className="w-24" />
                                            <col className="w-24" />
                                            <col className="w-32" />
                                            <col className="w-16" />
                                          </colgroup>
                                          <thead>
                                            <tr className="border-b border-white/20">
                                              <th className="px-4 py-1.5 print:py-3 text-left text-xs font-semibold text-white/80 uppercase tracking-wider">Studio Name</th>
                                              <th className="px-4 py-1.5 print:py-3 text-left text-xs font-semibold text-white/80 uppercase tracking-wider">Routine</th>
                                              <th className="px-4 py-1.5 print:py-3 text-left text-xs font-semibold text-white/80 uppercase tracking-wider">Score</th>
                                              <th className="px-4 py-1.5 print:py-3 text-left text-xs font-semibold text-white/80 uppercase tracking-wider">Age Division</th>
                                              <th className="px-4 py-1.5 print:py-3 text-left text-xs font-semibold text-white/80 uppercase tracking-wider">Category</th>
                                              <th className="px-4 py-1.5 print:py-3 text-left text-xs font-semibold text-white/80 uppercase tracking-wider">Performance Division</th>
                                              <th className="px-2 py-1.5 print:py-3 text-left text-xs font-semibold text-white/80 uppercase tracking-wider">Remove</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {winners.map((routine) => (
                                              <tr key={routine.routineId} className="border-b border-white/10 hover:bg-white/5 transition-colors">
                                                <td className="px-4 py-1.5 print:py-4 text-sm font-bold text-white break-words">{routine.studio}</td>
                                                <td className="px-4 py-1.5 print:py-4 text-sm text-white/90 break-words">
                                                  <div className="font-medium">{routine.routineName}</div>
                                                </td>
                                                <td className="px-4 py-1.5 print:py-4 whitespace-nowrap text-sm text-left score-cell font-bold text-white/80">
                                                  {routine.award === 'Not Scored' ? (
                                                    <span className="font-bold text-gray-400">Not Scored</span>
                                                  ) : showScores ? (
                                                    (() => {
                                                      const dropScoresText = routine.flexibleScoreData?.droppedScores && routine.flexibleScoreData.droppedScores.length > 0
                                                        ? `: ${routine.flexibleScoreData.droppedScores.map((d: any) => d.totalScore || d.originalScore?.totalScore || 0).join(', ')}`
                                                        : '';
                                                      return (
                                                        <span className="font-bold">
                                                          {routine.award} ({routine.finalScore.toFixed(0)}{dropScoresText})
                                                        </span>
                                                      );
                                                    })()
                                                  ) : (
                                                    <span className="font-bold">{routine.award}</span>
                                                  )}
                                                </td>
                                                <td className="px-4 py-1.5 print:py-4 whitespace-nowrap text-sm text-white/80">{routine.ageGroup}</td>
                                                <td className="px-4 py-1.5 print:py-4 whitespace-nowrap text-sm text-white/80">{routine.category}</td>
                                                <td className="px-4 py-1.5 print:py-4 whitespace-nowrap text-sm text-white/80">{routine.division}</td>
                                                <td className="px-2 py-1.5 print:py-3 text-left">
                                                  <button
                                                    onClick={() => removeFromWinners(routine.routineId)}
                                                    className="w-8 h-8 rounded-full bg-gradient-to-r from-red-500/30 to-rose-500/30 border border-red-400/50 text-red-200 hover:from-red-500/40 hover:to-rose-500/40 transition-all duration-200 flex items-center justify-center"
                                                  >
                                                    <span className="text-lg font-bold">×</span>
                                                  </button>
                                                </td>
                                              </tr>
                                            ))}
                                          </tbody>
                                        </table>
                                      </div>
                                    )}
                                  </StageCard>
                                </div>
                              );
                            };

                            return sortedAgeGroups.flatMap(ageGroup => {
                              const ageGroupRoutines = groupedByAge[ageGroup];
                              const splitColumns = splitAgeGroups[ageGroup];
                              
                              // Check if this age group is split into two columns
                              if (splitColumns && Array.isArray(splitColumns) && splitColumns.length >= 2) {
                                const column1Divisions = splitColumns[0] || [];
                                const column2Divisions = splitColumns[1] || [];
                                
                                // Filter routines for column 1
                                const column1Routines = column1Divisions.length > 0
                                  ? ageGroupRoutines.filter(routine => column1Divisions.includes(routine.division))
                                  : [];
                                
                                // Filter routines for column 2
                                const column2Routines = column2Divisions.length > 0
                                  ? ageGroupRoutines.filter(routine => column2Divisions.includes(routine.division))
                                  : [];
                                
                                // Filter winners for each column
                                const column1Winners = selectedWinners.filter(w => 
                                  w.ageGroup === ageGroup && column1Divisions.includes(w.division)
                                );
                                const column2Winners = selectedWinners.filter(w => 
                                  w.ageGroup === ageGroup && column2Divisions.includes(w.division)
                                );
                                
                                const sections = [];
                                
                                // Column 1 section
                                if (column1Routines.length > 0 || column1Winners.length > 0) {
                                  const column1Label = `${ageGroup} ${awardDisplayName} - ${getSplitColumnLabel(column1Divisions)}`;
                                  sections.push(renderWinnersSection(column1Routines, column1Label, column1Winners, `age-${ageGroup}-col1`));
                                }
                                
                                // Column 2 section
                                if (column2Routines.length > 0 || column2Winners.length > 0) {
                                  const column2Label = `${ageGroup} ${awardDisplayName} - ${getSplitColumnLabel(column2Divisions)}`;
                                  sections.push(renderWinnersSection(column2Routines, column2Label, column2Winners, `age-${ageGroup}-col2`));
                                }
                                
                                return sections;
                              } else {
                                // Not split - show all routines for this age group
                                const ageGroupWinners = selectedWinners.filter(w => w.ageGroup === ageGroup);
                                const label = `${ageGroup} ${awardDisplayName}`;
                                return [renderWinnersSection(ageGroupRoutines, label, ageGroupWinners, `age-${ageGroup}`)];
                              }
                            });
                          } else {
                            // Studio Standouts - Traditional two-section layout
                            // Get the display name from custom awards
                            const customAward = customAwards.find(award => {
                              if (!award.enabled) return false;
                              return reportType.startsWith(award.templateType);
                            });
                            
                            const sortedStudioRoutines = [...filteredRoutines].sort((a, b) => {
                              // First compare studio names alphabetically
                              const studioA = (a.studio || '').toLowerCase();
                              const studioB = (b.studio || '').toLowerCase();
                              if (studioA !== studioB) {
                                return studioA.localeCompare(studioB);
                              }
                              
                              // If same studio, compare final scores (highest to lowest)
                              if (a.finalScore !== b.finalScore) {
                                return b.finalScore - a.finalScore;
                              }
                              
                              // If final scores are tied, use drop scores for tie-breaking
                              const aDropScore = a.flexibleScoreData?.droppedScores?.[0]?.totalScore || 
                                                a.flexibleScoreData?.droppedScores?.[0]?.originalScore?.totalScore || 0;
                              const bDropScore = b.flexibleScoreData?.droppedScores?.[0]?.totalScore || 
                                                b.flexibleScoreData?.droppedScores?.[0]?.originalScore?.totalScore || 0;
                              
                              return bDropScore - aDropScore; // Higher drop score wins
                            });

                            return (
                              <>
                                {/* Eligible Routines Section */}
                                <StageCard className="min-h-0 p-6 print:hidden" glowColor="blue">
                                  <div className="flex justify-between items-center mb-6">
                                    <h3 className="text-xl font-black text-white">ELIGIBLE ROUTINES</h3>
                                    <span className="text-xs px-2.5 py-1 rounded-full bg-gradient-to-r from-blue-500/20 to-indigo-500/20 border border-blue-400/40 text-blue-300 backdrop-blur-xl">
                                      <span className="font-bold">{filteredRoutines.length}</span> Eligible
                                    </span>
                                  </div>
                                  
                                  <div className="overflow-x-auto">
                                    <table className="min-w-full text-sm table-fixed">
                                      <colgroup>
                                        <col className="w-44" />
                                        <col className="w-56" />
                                        <col className="w-48" />
                                        <col className="w-24" />
                                        <col className="w-24" />
                                        <col className="w-32" />
                                        <col className="w-16" />
                                      </colgroup>
                                      <thead>
                                        <tr className="border-b border-white/20">
                                          <th className="px-4 py-1.5 print:py-3 text-left text-xs font-semibold text-white/80 uppercase tracking-wider">Studio Name</th>
                                          <th className="px-4 py-1.5 print:py-3 text-left text-xs font-semibold text-white/80 uppercase tracking-wider">Routine</th>
                                          <th className="px-4 py-1.5 print:py-3 text-left text-xs font-semibold text-white/80 uppercase tracking-wider">Score</th>
                                          <th className="px-4 py-1.5 print:py-3 text-left text-xs font-semibold text-white/80 uppercase tracking-wider">Age Division</th>
                                          <th className="px-4 py-1.5 print:py-3 text-left text-xs font-semibold text-white/80 uppercase tracking-wider">Category</th>
                                          <th className="px-4 py-1.5 print:py-3 text-left text-xs font-semibold text-white/80 uppercase tracking-wider">Performance Division</th>
                                          <th className="px-2 py-1.5 print:py-3 text-left text-xs font-semibold text-white/80 uppercase tracking-wider">Add</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {sortedStudioRoutines.map((routine, index) => {
                                          const isAlreadySelected = selectedWinners.find(w => w.routineId === routine.routineId);
                                          return (
                                            <tr key={routine.routineId} className={`border-b border-white/10 ${isAlreadySelected ? 'opacity-50' : 'hover:bg-white/5'} transition-colors`}>
                                              <td className="px-4 py-1.5 print:py-4 text-sm font-bold text-white break-words">{routine.studio}</td>
                                              <td className="px-4 py-1.5 print:py-4 text-sm text-white/90 break-words">
                                                <div className="font-medium">
                                                  {routine.routineName}
                                                </div>
                                              </td>
                                              <td className="px-4 py-1.5 print:py-4 whitespace-nowrap text-sm text-left score-cell font-bold text-white/80">
                                                {routine.award === 'Not Scored' ? (
                                                  <span className="font-bold text-gray-400">Not Scored</span>
                                                ) : showScores ? (
                                                  // Show award with score and drop scores inline
                                                  (() => {
                                                    const dropScoresText = routine.flexibleScoreData?.droppedScores && routine.flexibleScoreData.droppedScores.length > 0
                                                      ? `: ${routine.flexibleScoreData.droppedScores.map((d: any) => d.totalScore || d.originalScore?.totalScore || 0).join(', ')}`
                                                      : '';
                                                    return (
                                                      <span className="font-bold">
                                                        {routine.award} ({routine.finalScore.toFixed(0)}{dropScoresText})
                                                      </span>
                                                    );
                                                  })()
                                                ) : (
                                                  // Show only award name
                                                  <span className="font-bold">{routine.award}</span>
                                                )}
                                              </td>
                                              <td className="px-4 py-1.5 print:py-4 whitespace-nowrap text-sm text-white/80">{routine.ageGroup}</td>
                                              <td className="px-4 py-1.5 print:py-4 whitespace-nowrap text-sm text-white/80">{routine.category}</td>
                                              <td className="px-4 py-1.5 print:py-4 whitespace-nowrap text-sm text-white/80">{routine.division}</td>
                                              <td className="px-2 py-1.5 print:py-3 text-left">
                                                {isAlreadySelected ? (
                                                  <span className="text-green-400 text-xs">Added</span>
                                                ) : (
                                                  <button
                                                    onClick={() => addToWinners(routine)}
                                                    className="w-8 h-8 rounded-full bg-gradient-to-r from-green-500/30 to-emerald-500/30 border border-green-400/50 text-green-200 hover:from-green-500/40 hover:to-emerald-500/40 transition-all duration-200 flex items-center justify-center"
                                                  >
                                                    <span className="text-lg font-bold">+</span>
                                                  </button>
                                                )}
                                              </td>
                                            </tr>
                                          );
                                        })}
                                      </tbody>
                                    </table>
                                  </div>
                                </StageCard>

                                {/* Selected Winners Section */}
                                <StageCard className="min-h-0 p-6 print:hidden" glowColor="pink">
                                  <div className="flex justify-between items-center mb-6">
                                    <h3 className="text-xl font-black bg-gradient-to-r from-pink-400 via-rose-400 to-pink-500 bg-clip-text text-transparent">{customAward?.displayName || 'AWARD'} WINNERS</h3>
                                    <span className="text-xs px-2.5 py-1 rounded-full bg-gradient-to-r from-pink-500/20 to-rose-500/20 border border-pink-400/40 text-pink-300 backdrop-blur-xl">
                                      <span className="font-bold">{selectedWinners.length}</span> Selected
                                    </span>
                                  </div>
                                  
                                  {selectedWinners.length === 0 ? (
                                    <div className="text-center py-12">
                                      <Trophy className="w-16 h-16 mx-auto mb-4 text-pink-400/50" />
                                      <p className="text-white/50">No winners selected yet. Use the + buttons above to add routines.</p>
                                    </div>
                                  ) : (
                                    <div className="overflow-x-auto">
                                      <table className="min-w-full text-sm table-fixed">
                                        <colgroup>
                                          <col className="w-44" />
                                          <col className="w-56" />
                                          <col className="w-48" />
                                          <col className="w-24" />
                                          <col className="w-24" />
                                          <col className="w-32" />
                                          <col className="w-16" />
                                        </colgroup>
                                        <thead>
                                          <tr className="border-b border-white/20">
                                            <th className="px-4 py-1.5 print:py-3 text-left text-xs font-semibold text-white/80 uppercase tracking-wider">Studio Name</th>
                                            <th className="px-4 py-1.5 print:py-3 text-left text-xs font-semibold text-white/80 uppercase tracking-wider">Routine</th>
                                            <th className="px-4 py-1.5 print:py-3 text-left text-xs font-semibold text-white/80 uppercase tracking-wider">Score</th>
                                            <th className="px-4 py-1.5 print:py-3 text-left text-xs font-semibold text-white/80 uppercase tracking-wider">Age Division</th>
                                            <th className="px-4 py-1.5 print:py-3 text-left text-xs font-semibold text-white/80 uppercase tracking-wider">Category</th>
                                            <th className="px-4 py-1.5 print:py-3 text-left text-xs font-semibold text-white/80 uppercase tracking-wider">Performance Division</th>
                                            <th className="px-2 py-1.5 print:py-3 text-left text-xs font-semibold text-white/80 uppercase tracking-wider">Remove</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {[...selectedWinners].sort((a, b) => {
                                            // First compare studio names alphabetically
                                            const studioA = (a.studio || '').toLowerCase();
                                            const studioB = (b.studio || '').toLowerCase();
                                            if (studioA !== studioB) {
                                              return studioA.localeCompare(studioB);
                                            }
                                            
                                            // If same studio, compare final scores (highest to lowest)
                                            if (a.finalScore !== b.finalScore) {
                                              return b.finalScore - a.finalScore;
                                            }
                                            
                                            // If final scores are tied, use drop scores for tie-breaking
                                            const aDropScore = a.flexibleScoreData?.droppedScores?.[0]?.totalScore || 
                                                              a.flexibleScoreData?.droppedScores?.[0]?.originalScore?.totalScore || 0;
                                            const bDropScore = b.flexibleScoreData?.droppedScores?.[0]?.totalScore || 
                                                              b.flexibleScoreData?.droppedScores?.[0]?.originalScore?.totalScore || 0;
                                            
                                            return bDropScore - aDropScore;
                                          }).map((routine, index) => (
                                            <tr key={routine.routineId} className="border-b border-white/10 hover:bg-white/5 transition-colors">
                                              <td className="px-4 py-1.5 print:py-4 text-sm font-bold text-white break-words">{routine.studio}</td>
                                              <td className="px-4 py-1.5 print:py-4 text-sm text-white/90 break-words">
                                                <div className="font-medium">
                                                  {routine.routineName}
                                                </div>
                                              </td>
                                              <td className="px-4 py-1.5 print:py-4 whitespace-nowrap text-sm text-left score-cell font-bold text-white/80">
                                                {routine.award === 'Not Scored' ? (
                                                  <span className="font-bold text-gray-400">Not Scored</span>
                                                ) : showScores ? (
                                                  // Show award with score and drop scores inline
                                                  (() => {
                                                    const dropScoresText = routine.flexibleScoreData?.droppedScores && routine.flexibleScoreData.droppedScores.length > 0
                                                      ? `: ${routine.flexibleScoreData.droppedScores.map((d: any) => d.totalScore || d.originalScore?.totalScore || 0).join(', ')}`
                                                      : '';
                                                    return (
                                                      <span className="font-bold">
                                                        {routine.award} ({routine.finalScore.toFixed(0)}{dropScoresText})
                                                      </span>
                                                    );
                                                  })()
                                                ) : (
                                                  // Show only award name
                                                  <span className="font-bold">{routine.award}</span>
                                                )}
                                              </td>
                                              <td className="px-4 py-1.5 print:py-4 whitespace-nowrap text-sm text-white/80">{routine.ageGroup}</td>
                                              <td className="px-4 py-1.5 print:py-4 whitespace-nowrap text-sm text-white/80">{routine.category}</td>
                                              <td className="px-4 py-1.5 print:py-4 whitespace-nowrap text-sm text-white/80">{routine.division}</td>
                                              <td className="px-2 py-1.5 print:py-3 text-left">
                                                <button
                                                  onClick={() => removeFromWinners(routine.routineId)}
                                                  className="w-8 h-8 rounded-full bg-gradient-to-r from-red-500/30 to-rose-500/30 border border-red-400/50 text-red-200 hover:from-red-500/40 hover:to-rose-500/40 transition-all duration-200 flex items-center justify-center"
                                                >
                                                  <span className="text-lg font-bold">×</span>
                                                </button>
                                              </td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>
                                  )}
                                </StageCard>
                              </>
                            );
                          }
                        })()}
                      </div>
                    </>
                  );
                }
                
                // Normal mode - existing layout
                return (
                  <>
                    <StageCard className="min-h-0 p-6 print:hidden" glowColor="purple">
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-xl font-black text-white">
                          {reportType === 'highScoreByAge' || reportType.startsWith('highScoreByAge') ? 'HIGH SCORE BY AGE' : 
                           reportType === 'high-score-division' || reportType === 'highScoreByDivision' || reportType.startsWith('highScoreByDivision') ? 'HIGH SCORE BY PERFORMANCE DIVISION' :
                           reportType !== 'adjudication' ? 'HIGH SCORE REPORT' : 'ADJUDICATION AWARDS'}
                  </h3>
                        <div className="flex items-center gap-3">
                          {/* Stage filter indicator */}
                          {!selectedStages.includes('all') && availableStages.length > 0 && (
                            <span className="text-xs px-2.5 py-1 rounded-full bg-gradient-to-r from-blue-500/20 to-indigo-500/20 border border-blue-400/40 text-blue-300 backdrop-blur-xl">
                              {selectedStages.length === 1 
                                ? availableStages.find(s => s.id === selectedStages[0])?.name || 'Stage Filter'
                                : `${selectedStages.length} Stages`
                              }
                            </span>
                          )}
                  <span className="text-xs px-2.5 py-1 rounded-full bg-gradient-to-r from-purple-500/20 to-pink-500/20 border border-purple-400/40 text-purple-300 backdrop-blur-xl">
                            <span className="font-bold">{filteredRoutines.length}</span> {filteredRoutines.length === 1 ? 'Award' : 'Awards'}
                  </span>
                        </div>
                </div>
                
                      {(() => {
                        if (reportType === 'highScoreByAge' || reportType.startsWith('highScoreByAge')) {
                          // Group routines by age division AND category for High Score by Age reports
                          const groupedByAgeAndCategory: { [ageGroup: string]: { [category: string]: AwardedRoutine[] } } = {};
                          filteredRoutines.forEach(routine => {
                      if (!groupedByAgeAndCategory[routine.ageGroup]) {
                        groupedByAgeAndCategory[routine.ageGroup] = {};
                      }
                      if (!groupedByAgeAndCategory[routine.ageGroup][routine.category]) {
                        groupedByAgeAndCategory[routine.ageGroup][routine.category] = [];
                      }
                      groupedByAgeAndCategory[routine.ageGroup][routine.category].push(routine);
                    });
                    
                    // Sort age groups by order (youngest to oldest)
                    const sortedAgeGroups = Object.keys(groupedByAgeAndCategory).sort((a, b) => 
                      getAgeDivisionOrder(a) - getAgeDivisionOrder(b)
                    );

                    return sortedAgeGroups.map(ageGroup => {
                      // Sort categories within each age group (Solo, Duo/Trio, Small Group, etc.)
                      const sortedCategories = Object.keys(groupedByAgeAndCategory[ageGroup]).sort((a, b) => 
                        getCategoryOrder(a) - getCategoryOrder(b)
                      );

                      return sortedCategories.map(category => (
                        <div key={`age-category-${ageGroup}-${category}`} className="mb-4 print:mb-8">
                          {/* Age Group + Category Header - Above the table */}
                          <div className="mb-2 print:mb-4 p-2 print:p-4 bg-gradient-to-r from-purple-600/30 to-pink-600/30 border border-purple-400/50 rounded-lg">
                            <h4 className="text-base print:text-lg font-bold text-white text-center">
                              {ageGroup} {category} High Score
                            </h4>
                          </div>
                        
                        {/* Table for this age group */}
                        <div className="overflow-x-auto">
                  <table className="min-w-full text-sm table-fixed">
                    <colgroup>
                      {showsPlacementColumn(reportType) && <col className="w-12" />}
                      <col className={showsStudioNameColumn(reportType) ? "w-48" : "w-16"} />
                      {reportType === 'adjudication' && <col className="w-48" />}
                      <col className="w-64" />
                      <col className="w-32" />
                      <col className="w-24" />
                      <col className="w-24" />
                      <col className="w-32" />
                    </colgroup>
                    <thead className="border-b border-white/20">
                      <tr>
                        {showsPlacementColumn(reportType) && (
                                  <th className="px-2 py-1.5 print:py-3 text-left text-white/80 font-bold text-xs uppercase tracking-wider">
                            Placement
                          </th>
                        )}
                        <th className="px-4 py-1.5 print:py-3 text-left text-white/80 font-bold text-xs uppercase tracking-wider">
                          {showsStudioNameColumn(reportType) ? 'Studio Name' : '#'}
                        </th>
                        {reportType === 'adjudication' && (
                          <th className="px-4 py-1.5 print:py-3 text-left text-white/80 font-bold text-xs uppercase tracking-wider">
                            Studio
                          </th>
                        )}
                        <th className="px-4 py-1.5 print:py-3 text-left text-white/80 font-bold text-xs uppercase tracking-wider">
                          Routine
                        </th>
                                <th className="px-4 py-1.5 print:py-3 text-left text-white/80 font-bold text-xs uppercase tracking-wider">
                          Score
                        </th>
                        <th className="px-4 py-1.5 print:py-3 text-left text-white/80 font-bold text-xs uppercase tracking-wider">
                          Age Division
                        </th>
                        <th className="px-4 py-1.5 print:py-3 text-left text-white/80 font-bold text-xs uppercase tracking-wider">
                          Category
                        </th>
                        <th className="px-4 py-1.5 print:py-3 text-left text-white/80 font-bold text-xs uppercase tracking-wider">
                          Performance Division
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                              {groupedByAgeAndCategory[ageGroup][category].map((routine, index) => {
                        // For high score reports with placement, use placement-based alternating colors
                        let isAlternate = false;
                        
                        if (usesPlacementBasedColors(reportType)) {
                                  // Placement-based colors: odd placements (1st, 3rd, 5th, etc.) = white, even placements (2nd, 4th, 6th, etc.) = gray
                                  isAlternate = routine.rank % 2 === 0; // Even placements get gray background
                                } else {
                                  // For all other reports, use index-based alternating
                                  isAlternate = index % 2 === 1;
                                }
                                
                                return (
                                  <motion.tr 
                                    key={routine.routineId}
                                    className={`border-b border-white/10 transition-colors ${
                                      isAlternate ? 'bg-white/5' : 'bg-transparent'
                                    } ${
                                      usesPlacementBasedColors(reportType) 
                                        ? (isAlternate ? 'print:bg-gray-200' : 'print:bg-white')
                                        : (isAlternate ? 'print:bg-gray-200' : 'print:bg-white')
                                    }`}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.3, delay: index * 0.05 }}
                                  >
                                    {showsPlacementColumn(reportType) && (
                                      <td className="px-2 py-1.5 print:py-4 whitespace-nowrap text-sm font-bold text-white">
                                          {routine.placement?.replace(/\D/g, '')}
                                      </td>
                                    )}
                                    <td className="px-4 py-1.5 print:py-4 text-sm font-bold text-white break-words">
                                      {showsStudioNameColumn(reportType) ? routine.studio : routine.routineId}
                                    </td>
                                    {reportType === 'adjudication' && (
                                      <td className="px-4 py-1.5 print:py-4 whitespace-nowrap text-sm font-medium text-white/80">
                                        {routine.studio}
                                      </td>
                                    )}
                                    <td className="px-4 py-1.5 print:py-4 text-sm text-white/90 break-words">
                                      <div className="font-medium">
                                        {routine.routineName}
                                        {routine.performer && routine.performer.trim() !== '' && !routine.routineName.includes(routine.performer) && 
                                         ((reportType === 'adjudication' && routine.division === 'Solo') || (reportType !== 'adjudication' && routine.category === 'Solo')) && (
                                          <span className="text-white/60"> ({routine.performer})</span>
                                        )}
                                      </div>
                                    </td>
                                    <td className="px-4 py-1.5 print:py-4 whitespace-nowrap text-sm text-left score-cell text-white/80 font-bold">
                                      {showScores ? (
                                        // Show award with score and drop scores inline
                                        (() => {
                                          const dropScoresText = routine.flexibleScoreData?.droppedScores && routine.flexibleScoreData.droppedScores.length > 0
                                            ? `: ${routine.flexibleScoreData.droppedScores.map((d: any) => d.totalScore || d.originalScore?.totalScore || 0).join(', ')}`
                                            : '';
                                          return (
                                            <span className="font-bold">
                                              {routine.award} ({routine.finalScore.toFixed(0)}{dropScoresText})
                                            </span>
                                          );
                                        })()
                                      ) : (
                                        // Show only award name
                                        <span className="font-bold">{routine.award}</span>
                                      )}
                                    </td>
                                    <td className="px-4 py-1.5 print:py-4 whitespace-nowrap text-sm text-white/80">
                                      {routine.ageGroup}
                                    </td>
                                    <td className="px-4 py-1.5 print:py-4 whitespace-nowrap text-sm text-white/80">
                                      {reportType === 'adjudication' ? routine.division : routine.category}
                                    </td>
                                    <td className="px-4 py-1.5 print:py-4 whitespace-nowrap text-sm text-white/80">
                                      {reportType === 'adjudication' ? routine.category : routine.division}
                                    </td>
                                  </motion.tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                      ));
                    });
                  } else if (reportType === 'high-score-division' || reportType === 'highScoreByDivision' || reportType.startsWith('highScoreByDivision')) {
                          // Group routines by age division AND performance division for High Score by Performance Division reports
                          const groupedByAgeAndDivision: { [ageGroup: string]: { [performanceDivision: string]: AwardedRoutine[] } } = {};
                          filteredRoutines.forEach(routine => {
                      if (!groupedByAgeAndDivision[routine.ageGroup]) {
                        groupedByAgeAndDivision[routine.ageGroup] = {};
                      }
                      if (!groupedByAgeAndDivision[routine.ageGroup][routine.division]) {
                        groupedByAgeAndDivision[routine.ageGroup][routine.division] = [];
                      }
                      groupedByAgeAndDivision[routine.ageGroup][routine.division].push(routine);
                    });
                    
                    // Sort age groups by order (youngest to oldest)
                    const sortedAgeGroups = Object.keys(groupedByAgeAndDivision).sort((a, b) => 
                      getAgeDivisionOrder(a) - getAgeDivisionOrder(b)
                    );

                    return sortedAgeGroups.map(ageGroup => {
                      // Sort performance divisions within each age group alphabetically
                      const sortedPerformanceDivisions = Object.keys(groupedByAgeAndDivision[ageGroup]).sort();

                      return sortedPerformanceDivisions.map(performanceDivision => (
                        <div key={`age-division-${ageGroup}-${performanceDivision}`} className="mb-4 print:mb-8">
                          {/* Age Group + Performance Division Header - Above the table */}
                          <div className="mb-2 print:mb-4 p-2 print:p-4 bg-gradient-to-r from-purple-600/30 to-pink-600/30 border border-purple-400/50 rounded-lg">
                            <h4 className="text-base print:text-lg font-bold text-white text-center">
                              {ageGroup} {performanceDivision} High Score
                            </h4>
                          </div>
                        
                        {/* Table for this performance division */}
                        <div className="overflow-x-auto">
                  <table className="min-w-full text-sm table-fixed">
                    <colgroup>
                      {showsPlacementColumn(reportType) && <col className="w-12" />}
                      <col className={showsStudioNameColumn(reportType) ? "w-48" : "w-16"} />
                      {reportType === 'adjudication' && <col className="w-48" />}
                      <col className="w-64" />
                      <col className="w-32" />
                      <col className="w-24" />
                      <col className="w-24" />
                      <col className="w-32" />
                    </colgroup>
                    <thead className="border-b border-white/20">
                              <tr>
                                {showsPlacementColumn(reportType) && (
                                  <th className="px-2 py-1.5 print:py-3 text-left text-white/80 font-bold text-xs uppercase tracking-wider">
                                    Placement
                                  </th>
                                )}
                                <th className="px-4 py-1.5 print:py-3 text-left text-white/80 font-bold text-xs uppercase tracking-wider">
                                  {showsStudioNameColumn(reportType) ? 'Studio Name' : '#'}
                                </th>
                                {reportType === 'adjudication' && (
                                  <th className="px-4 py-1.5 print:py-3 text-left text-white/80 font-bold text-xs uppercase tracking-wider">
                                    Studio
                                  </th>
                                )}
                                <th className="px-4 py-1.5 print:py-3 text-left text-white/80 font-bold text-xs uppercase tracking-wider">
                                  Routine
                                </th>
                                <th className="px-4 py-1.5 print:py-3 text-left text-white/80 font-bold text-xs uppercase tracking-wider">
                                  Score
                                </th>
                                <th className="px-4 py-1.5 print:py-3 text-left text-white/80 font-bold text-xs uppercase tracking-wider">
                                  Age Division
                                </th>
                                <th className="px-4 py-1.5 print:py-3 text-left text-white/80 font-bold text-xs uppercase tracking-wider">
                                  Category
                                </th>
                                <th className="px-4 py-1.5 print:py-3 text-left text-white/80 font-bold text-xs uppercase tracking-wider">
                                  Performance Division
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {groupedByAgeAndDivision[ageGroup][performanceDivision].map((routine, index) => {
                                // For high score reports with placement, use placement-based alternating colors
                                let isAlternate = false;
                                
                                if (usesPlacementBasedColors(reportType)) {
                                  // Placement-based colors: odd placements (1st, 3rd, 5th, etc.) = white, even placements (2nd, 4th, 6th, etc.) = gray
                                  isAlternate = routine.rank % 2 === 0; // Even placements get gray background
                        } else {
                          // For all other reports, use index-based alternating
                          isAlternate = index % 2 === 1;
                        }
                        
                        return (
                          <motion.tr 
                            key={routine.routineId}
                            className={`border-b border-white/10 transition-colors ${
                              isAlternate ? 'bg-white/5' : 'bg-transparent'
                            } ${
                              usesPlacementBasedColors(reportType) 
                                        ? (isAlternate ? 'print:bg-gray-200' : 'print:bg-white')
                                : (isAlternate ? 'print:bg-gray-200' : 'print:bg-white')
                            }`}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.3, delay: index * 0.05 }}
                          >
                            {showsPlacementColumn(reportType) && (
                                      <td className="px-2 py-1.5 print:py-4 whitespace-nowrap text-sm font-bold text-white">
                                  {routine.placement?.replace(/\D/g, '')}
                              </td>
                            )}
                                    <td className="px-4 py-1.5 print:py-4 text-sm font-bold text-white break-words">
                              {showsStudioNameColumn(reportType) ? routine.studio : routine.routineId}
                            </td>
                            {reportType === 'adjudication' && (
                              <td className="px-4 py-1.5 print:py-4 whitespace-nowrap text-sm font-medium text-white/80">
                                {routine.studio}
                              </td>
                            )}
                                    <td className="px-4 py-1.5 print:py-4 text-sm text-white/90 break-words">
                              <div className="font-medium">
                                {routine.routineName}
                                        {routine.performer && routine.performer.trim() !== '' && !routine.routineName.includes(routine.performer) && 
                                         ((reportType === 'adjudication' && routine.division === 'Solo') || (reportType !== 'adjudication' && routine.category === 'Solo')) && (
                                  <span className="text-white/60"> ({routine.performer})</span>
                                )}
                              </div>
                            </td>
                                    <td className="px-4 py-1.5 print:py-4 whitespace-nowrap text-sm text-left score-cell text-white/80 font-bold">
                              {showScores ? (
                                // Show award with score and drop scores inline
                                (() => {
                                  const dropScoresText = routine.flexibleScoreData?.droppedScores && routine.flexibleScoreData.droppedScores.length > 0
                                    ? `: ${routine.flexibleScoreData.droppedScores.map((d: any) => d.totalScore || d.originalScore?.totalScore || 0).join(', ')}`
                                    : '';
                                  return (
                                    <span className="font-bold">
                                      {routine.award} ({routine.finalScore.toFixed(0)}{dropScoresText})
                                    </span>
                                  );
                                })()
                              ) : (
                                // Show only award name
                                <span className="font-bold">{routine.award}</span>
                              )}
                            </td>
                            <td className="px-4 py-1.5 print:py-4 whitespace-nowrap text-sm text-white/80">
                              {routine.ageGroup}
                            </td>
                            <td className="px-4 py-1.5 print:py-4 whitespace-nowrap text-sm text-white/80">
                                      {reportType === 'adjudication' ? routine.division : routine.category}
                            </td>
                            <td className="px-4 py-1.5 print:py-4 whitespace-nowrap text-sm text-white/80">
                                      {reportType === 'adjudication' ? routine.category : routine.division}
                            </td>
                          </motion.tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                      </div>
                      ));
                    });
                  } else if (reportType === 'greatestHits' || reportType.startsWith('greatestHits')) {
                    // Group routines by age division for Greatest Hits
                    const groupedByAge: { [ageGroup: string]: AwardedRoutine[] } = {};
                    filteredRoutines.forEach(routine => {
                      if (!groupedByAge[routine.ageGroup]) {
                        groupedByAge[routine.ageGroup] = [];
                      }
                      groupedByAge[routine.ageGroup].push(routine);
                    });
                    
                    // Sort age groups by order (youngest to oldest)
                    const sortedAgeGroups = Object.keys(groupedByAge).sort((a, b) => 
                      getAgeDivisionOrder(a) - getAgeDivisionOrder(b)
                    );

                    // Get the display name and split configuration from custom awards
                    const customAward = customAwards.find(award => {
                      if (!award.enabled) return false;
                      return reportType.startsWith(award.templateType);
                    });
                    const awardDisplayName = customAward?.displayName || 'Award';
                    const splitAgeGroups = customAward?.config?.splitAgeGroups || {};

                    // Helper function to render a table for routines
                    const renderRoutineTable = (routines: AwardedRoutine[], headerText: string, key: string) => {
                      if (routines.length === 0) return null;
                      
                      // Sort routines by score (highest to lowest)
                      const sortedRoutines = [...routines].sort((a, b) => {
                        if (a.finalScore !== b.finalScore) {
                          return b.finalScore - a.finalScore;
                        }
                        const aDropScore = a.flexibleScoreData?.droppedScores?.[0]?.totalScore || 
                                          a.flexibleScoreData?.droppedScores?.[0]?.originalScore?.totalScore || 0;
                        const bDropScore = b.flexibleScoreData?.droppedScores?.[0]?.totalScore || 
                                          b.flexibleScoreData?.droppedScores?.[0]?.originalScore?.totalScore || 0;
                        return bDropScore - aDropScore;
                      });

                      return (
                        <div key={key} className="mb-4 print:mb-8">
                          {/* Section Header */}
                          <div className="mb-2 print:mb-4 p-2 print:p-4 bg-gradient-to-r from-purple-600/30 to-pink-600/30 border border-purple-400/50 rounded-lg">
                            <h4 className="text-base print:text-lg font-bold text-white text-center">
                              {headerText}
                            </h4>
                          </div>
                        
                          {/* Table for this section */}
                          <div className="overflow-x-auto">
                            <table className="min-w-full text-sm table-fixed">
                              <colgroup>
                                <col className="w-48" />
                                <col className="w-64" />
                                <col className="w-32" />
                                <col className="w-24" />
                                <col className="w-24" />
                                <col className="w-32" />
                              </colgroup>
                              <thead className="border-b border-white/20">
                                <tr>
                                  <th className="px-4 py-1.5 print:py-3 text-left text-white/80 font-bold text-xs uppercase tracking-wider">
                                    Studio Name
                                  </th>
                                  <th className="px-4 py-1.5 print:py-3 text-left text-white/80 font-bold text-xs uppercase tracking-wider">
                                    Routine
                                  </th>
                                  <th className="px-4 py-1.5 print:py-3 text-left text-white/80 font-bold text-xs uppercase tracking-wider">
                                    Score
                                  </th>
                                  <th className="px-4 py-1.5 print:py-3 text-left text-white/80 font-bold text-xs uppercase tracking-wider">
                                    Age Division
                                  </th>
                                  <th className="px-4 py-1.5 print:py-3 text-left text-white/80 font-bold text-xs uppercase tracking-wider">
                                    Category
                                  </th>
                                  <th className="px-4 py-1.5 print:py-3 text-left text-white/80 font-bold text-xs uppercase tracking-wider">
                                    Performance Division
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {sortedRoutines.map((routine, index) => {
                                  const isAlternate = index % 2 === 1;
                                  
                                  return (
                                    <motion.tr 
                                      key={routine.routineId}
                                      className={`border-b border-white/10 transition-colors ${
                                        isAlternate ? 'bg-white/5' : 'bg-transparent'
                                      } ${
                                        isAlternate ? 'print:bg-gray-200' : 'print:bg-white'
                                      }`}
                                      initial={{ opacity: 0, y: 20 }}
                                      animate={{ opacity: 1, y: 0 }}
                                      transition={{ duration: 0.3, delay: index * 0.05 }}
                                    >
                                      <td className="px-4 py-1.5 print:py-4 text-sm font-bold text-white break-words">
                                        {routine.studio}
                                      </td>
                                      <td className="px-4 py-1.5 print:py-4 text-sm text-white/90 break-words">
                                        <div className="font-medium">
                                          {routine.routineName}
                                          {routine.performer && routine.performer.trim() !== '' && !routine.routineName.includes(routine.performer) && 
                                           ((reportType === 'adjudication' && routine.division === 'Solo') || (reportType !== 'adjudication' && routine.category === 'Solo')) && (
                                            <span className="text-white/60"> ({routine.performer})</span>
                                          )}
                                        </div>
                                      </td>
                                      <td className="px-4 py-1.5 print:py-4 whitespace-nowrap text-sm text-left score-cell font-bold text-white/80">
                                        {routine.award === 'Not Scored' ? (
                                          <span className="font-bold text-gray-400">Not Scored</span>
                                        ) : showScores ? (
                                          (() => {
                                            const dropScoresText = routine.flexibleScoreData?.droppedScores && routine.flexibleScoreData.droppedScores.length > 0
                                              ? `: ${routine.flexibleScoreData.droppedScores.map((d: any) => d.totalScore || d.originalScore?.totalScore || 0).join(', ')}`
                                              : '';
                                            return (
                                              <span className="font-bold">
                                                {routine.award} ({routine.finalScore.toFixed(0)}{dropScoresText})
                                              </span>
                                            );
                                          })()
                                        ) : (
                                          <span className="font-bold">{routine.award}</span>
                                        )}
                                      </td>
                                      <td className="px-4 py-1.5 print:py-4 whitespace-nowrap text-sm text-white/80">
                                        {routine.ageGroup}
                                      </td>
                                      <td className="px-4 py-1.5 print:py-4 whitespace-nowrap text-sm text-white/80">
                                        {routine.category}
                                      </td>
                                      <td className="px-4 py-1.5 print:py-4 whitespace-nowrap text-sm text-white/80">
                                        {routine.division}
                                      </td>
                                    </motion.tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      );
                    };

                    return sortedAgeGroups.flatMap(ageGroup => {
                      const ageGroupRoutines = groupedByAge[ageGroup];
                      const splitColumns = splitAgeGroups[ageGroup];
                      
                      // Check if this age group is split into two columns
                      if (splitColumns && Array.isArray(splitColumns) && splitColumns.length >= 2) {
                        const column1Divisions = splitColumns[0] || [];
                        const column2Divisions = splitColumns[1] || [];
                        
                        // Filter routines for column 1
                        const column1Routines = column1Divisions.length > 0
                          ? ageGroupRoutines.filter(routine => column1Divisions.includes(routine.division))
                          : [];
                        
                        // Filter routines for column 2
                        const column2Routines = column2Divisions.length > 0
                          ? ageGroupRoutines.filter(routine => column2Divisions.includes(routine.division))
                          : [];
                        
                        // Create sections for both columns
                        const sections = [];
                        
                        // Column 1 section
                        if (column1Routines.length > 0) {
                          const column1Label = `${ageGroup} ${awardDisplayName} - ${getSplitColumnLabel(column1Divisions)}`;
                          sections.push(renderRoutineTable(column1Routines, column1Label, `age-${ageGroup}-col1`));
                        }
                        
                        // Column 2 section
                        if (column2Routines.length > 0) {
                          const column2Label = `${ageGroup} ${awardDisplayName} - ${getSplitColumnLabel(column2Divisions)}`;
                          sections.push(renderRoutineTable(column2Routines, column2Label, `age-${ageGroup}-col2`));
                        }
                        
                        // If no routines in either column but age group is split, show empty or show all
                        if (sections.length === 0 && ageGroupRoutines.length > 0) {
                          // Fallback: show all routines if split columns don't have any
                          const fallbackLabel = `${ageGroup} ${awardDisplayName}`;
                          sections.push(renderRoutineTable(ageGroupRoutines, fallbackLabel, `age-${ageGroup}-fallback`));
                        }
                        
                        return sections;
                      } else {
                        // Not split - show all routines for this age group
                        const label = `${ageGroup} ${awardDisplayName}`;
                        return [renderRoutineTable(ageGroupRoutines, label, `age-${ageGroup}`)];
                      }
                    });
                  } else if (reportType === 'studioStandout' || reportType.startsWith('studioStandout')) {
                    // Studio Standouts - Single list (not grouped by age)
                    // Get the display name from custom awards
                    const customAward = customAwards.find(award => {
                      if (!award.enabled) return false;
                      return reportType.startsWith(award.templateType);
                    });
                    const awardDisplayName = customAward?.displayName || 'Studio Standouts';

                    // Sort all Studio Standout routines by studio name (alphabetically), then by score (highest to lowest)
                    const sortedStudioRoutines = [...filteredRoutines].sort((a, b) => {
                      // First compare studio names alphabetically
                      const studioA = (a.studio || '').toLowerCase();
                      const studioB = (b.studio || '').toLowerCase();
                      if (studioA !== studioB) {
                        return studioA.localeCompare(studioB);
                      }
                      
                      // If same studio, compare final scores (highest to lowest)
                      if (a.finalScore !== b.finalScore) {
                        return b.finalScore - a.finalScore;
                      }
                      
                      // If final scores are tied, use drop scores for tie-breaking
                      const aDropScore = a.flexibleScoreData?.droppedScores?.[0]?.totalScore || 
                                        a.flexibleScoreData?.droppedScores?.[0]?.originalScore?.totalScore || 0;
                      const bDropScore = b.flexibleScoreData?.droppedScores?.[0]?.totalScore || 
                                        b.flexibleScoreData?.droppedScores?.[0]?.originalScore?.totalScore || 0;
                      
                      return bDropScore - aDropScore; // Higher drop score wins
                    });

                    return (
                      <div className="mb-4 print:mb-8">
                        {/* Single Header - Above the table */}
                        <div className="mb-2 print:mb-4 p-2 print:p-4 bg-gradient-to-r from-purple-600/30 to-pink-600/30 border border-purple-400/50 rounded-lg">
                          <h4 className="text-base print:text-lg font-bold text-white text-center">
                            {awardDisplayName}
                          </h4>
                        </div>
                      
                      {/* Single table for all Studio Standouts */}
                      <div className="overflow-x-auto">
                        <table className="min-w-full text-sm table-fixed">
                          <colgroup>
                            <col className="w-44" />
                            <col className="w-56" />
                            <col className="w-48" />
                            <col className="w-24" />
                            <col className="w-24" />
                            <col className="w-32" />
                          </colgroup>
                          <thead className="border-b border-white/20">
                            <tr>
                              <th className="px-4 py-1.5 print:py-3 text-left text-white/80 font-bold text-xs uppercase tracking-wider">
                                Studio Name
                              </th>
                              <th className="px-4 py-1.5 print:py-3 text-left text-white/80 font-bold text-xs uppercase tracking-wider">
                                Routine
                              </th>
                              <th className="px-4 py-1.5 print:py-3 text-left text-white/80 font-bold text-xs uppercase tracking-wider">
                                Score
                              </th>
                              <th className="px-4 py-1.5 print:py-3 text-left text-white/80 font-bold text-xs uppercase tracking-wider">
                                Age Division
                              </th>
                              <th className="px-4 py-1.5 print:py-3 text-left text-white/80 font-bold text-xs uppercase tracking-wider">
                                Category
                              </th>
                              <th className="px-4 py-1.5 print:py-3 text-left text-white/80 font-bold text-xs uppercase tracking-wider">
                                Performance Division
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {sortedStudioRoutines.map((routine, index) => {
                              const isAlternate = index % 2 === 1;
                              
                              return (
                                <motion.tr 
                                  key={routine.routineId}
                                  className={`border-b border-white/10 transition-colors ${
                                    isAlternate ? 'bg-white/5' : 'bg-transparent'
                                  } ${
                                    isAlternate ? 'print:bg-gray-200' : 'print:bg-white'
                                  }`}
                                  initial={{ opacity: 0, y: 20 }}
                                  animate={{ opacity: 1, y: 0 }}
                                  transition={{ duration: 0.3, delay: index * 0.05 }}
                                >
                                  <td className="px-4 py-1.5 print:py-4 text-sm font-bold text-white break-words">
                                    {routine.studio}
                                  </td>
                                  <td className="px-4 py-1.5 print:py-4 text-sm text-white/90 break-words">
                                    <div className="font-medium">
                                      {routine.routineName}
                                      {routine.performer && routine.performer.trim() !== '' && !routine.routineName.includes(routine.performer) && 
                                       ((reportType === 'adjudication' && routine.division === 'Solo') || (reportType !== 'adjudication' && routine.category === 'Solo')) && (
                                        <span className="text-white/60"> ({routine.performer})</span>
                                      )}
                                    </div>
                                  </td>
                                  <td className="px-4 py-1.5 print:py-4 whitespace-nowrap text-sm text-left score-cell font-bold text-white/80">
                                    {routine.award === 'Not Scored' ? (
                                      <span className="font-bold text-gray-400">Not Scored</span>
                                    ) : showScores ? (
                                      // Show award with score and drop scores inline
                                      (() => {
                                        const dropScoresText = routine.flexibleScoreData?.droppedScores && routine.flexibleScoreData.droppedScores.length > 0
                                          ? `: ${routine.flexibleScoreData.droppedScores.map((d: any) => d.totalScore || d.originalScore?.totalScore || 0).join(', ')}`
                                          : '';
                                        return (
                                          <span className="font-bold">
                                            {routine.award} ({routine.finalScore.toFixed(0)}{dropScoresText})
                                          </span>
                                        );
                                      })()
                                    ) : (
                                      // Show only award name
                                      <span className="font-bold">{routine.award}</span>
                                    )}
                                  </td>
                                  <td className="px-4 py-1.5 print:py-4 whitespace-nowrap text-sm text-white/80">
                                    {routine.ageGroup}
                                  </td>
                                  <td className="px-4 py-1.5 print:py-4 whitespace-nowrap text-sm text-white/80">
                                    {routine.category}
                                  </td>
                                  <td className="px-4 py-1.5 print:py-4 whitespace-nowrap text-sm text-white/80">
                                    {routine.division}
                                  </td>
                                </motion.tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                    );
                  } else {
                    // Default single table for adjudication awards
                    return (
                      <div className="overflow-x-auto">
                        <table className="min-w-full text-sm table-fixed">
                          <colgroup>
                            {showsPlacementColumn(reportType) && <col className="w-12" />}
                            <col className={showsStudioNameColumn(reportType) ? "w-48" : "w-16"} />
                            {reportType === 'adjudication' && <col className="w-48" />}
                            <col className="w-64" />
                            <col className="w-32" />
                            <col className="w-24" />
                            <col className="w-24" />
                            <col className="w-32" />
                          </colgroup>
                          <thead className="border-b border-white/20">
                            <tr>
                              {showsPlacementColumn(reportType) && (
                                <th className="px-2 py-1.5 print:py-3 text-left text-white/80 font-bold text-xs uppercase tracking-wider">
                                  Placement
                                </th>
                              )}
                              <th className="px-4 py-1.5 print:py-3 text-left text-white/80 font-bold text-xs uppercase tracking-wider">
                                {showsStudioNameColumn(reportType) ? 'Studio Name' : '#'}
                              </th>
                              {reportType === 'adjudication' && (
                                <th className="px-4 py-1.5 print:py-3 text-left text-white/80 font-bold text-xs uppercase tracking-wider">
                                  Studio
                                </th>
                              )}
                              <th className="px-4 py-1.5 print:py-3 text-left text-white/80 font-bold text-xs uppercase tracking-wider">
                                Routine
                              </th>
                              <th className={`px-4 py-1.5 print:py-3 text-left font-bold text-xs uppercase tracking-wider ${
                                reportType === 'adjudication' ? 'text-red-400 print:text-red-600' : 'text-white/80'
                              }`}>
                                Score
                              </th>
                              <th className="px-4 py-1.5 print:py-3 text-left text-white/80 font-bold text-xs uppercase tracking-wider">
                                Age Division
                              </th>
                              <th className="px-4 py-1.5 print:py-3 text-left text-white/80 font-bold text-xs uppercase tracking-wider">
                                Category
                              </th>
                              <th className="px-4 py-1.5 print:py-3 text-left text-white/80 font-bold text-xs uppercase tracking-wider">
                                Performance Division
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {filteredRoutines.map((routine, index) => {
                              // For high score reports with placement, use placement-based alternating colors
                              let isAlternate = false;
                              
                              if (usesPlacementBasedColors(reportType)) {
                                // Placement-based colors: odd placements (1st, 3rd, 5th, etc.) = white, even placements (2nd, 4th, 6th, etc.) = gray
                                isAlternate = routine.rank % 2 === 0; // Even placements get gray background
                              } else {
                                // For all other reports, use index-based alternating
                                isAlternate = index % 2 === 1;
                              }
                              
                              return (
                                <motion.tr 
                                  key={`${routine.routineId}-${index}`}
                                  className={`border-b border-white/10 transition-colors ${
                                    isAlternate ? 'bg-white/5' : 'bg-transparent'
                                  } ${
                                    usesPlacementBasedColors(reportType) 
                                      ? (isAlternate ? 'print:bg-gray-200' : 'print:bg-white')
                                      : (isAlternate ? 'print:bg-gray-200' : 'print:bg-white')
                                  }`}
                                  initial={{ opacity: 0, y: 20 }}
                                  animate={{ opacity: 1, y: 0 }}
                                  transition={{ duration: 0.3, delay: index * 0.05 }}
                                >
                                  {showsPlacementColumn(reportType) && (
                                    <td className="px-2 py-1.5 print:py-4 whitespace-nowrap text-sm font-bold text-white">
                                        {routine.placement?.replace(/\D/g, '')}
                                    </td>
                                  )}
                            <td className="px-4 py-1.5 print:py-4 text-sm font-bold text-white break-words">
                              {showsStudioNameColumn(reportType) ? routine.studio : routine.routineId}
                            </td>
                            {reportType === 'adjudication' && (
                              <td className="px-4 py-1.5 print:py-4 text-sm font-medium text-white/80 break-words">
                                {routine.studio}
                              </td>
                            )}
                            <td className="px-4 py-1.5 print:py-4 text-sm text-white/90 break-words">
                              <div className="font-medium">
                                {routine.routineName}
                                      {routine.performer && routine.performer.trim() !== '' && !routine.routineName.includes(routine.performer) && 
                                       ((reportType === 'adjudication' && routine.division === 'Solo') || (reportType !== 'adjudication' && routine.category === 'Solo')) && (
                                  <span className="text-white/60"> ({routine.performer})</span>
                                )}
                              </div>
                            </td>
                                  <td className={`px-4 py-1.5 print:py-4 whitespace-nowrap text-sm text-left score-cell font-bold ${
                                    reportType === 'adjudication' ? 'text-red-400 print:text-red-600 print:!text-red-600' : 'text-white/80'
                                  }`}>
                                    {routine.award === 'Not Scored' ? (
                                      <span className="font-bold text-gray-400">Not Scored</span>
                                    ) : showScores ? (
                                      // Show award with score and drop scores inline
                                      (() => {
                                        const dropScoresText = routine.flexibleScoreData?.droppedScores && routine.flexibleScoreData.droppedScores.length > 0
                                          ? `: ${routine.flexibleScoreData.droppedScores.map((d: any) => d.totalScore || d.originalScore?.totalScore || 0).join(', ')}`
                                          : '';
                                        return (
                                          <span className="font-bold">
                                            {routine.award} ({routine.finalScore.toFixed(0)}{dropScoresText})
                                          </span>
                                        );
                                      })()
                                    ) : (
                                      // Show only award name
                                      <span className="font-bold">{routine.award}</span>
                                    )}
                                  </td>
                                  <td className="px-4 py-1.5 print:py-4 whitespace-nowrap text-sm text-white/80">
                                    {routine.ageGroup}
                                  </td>
                                  <td className="px-4 py-1.5 print:py-4 whitespace-nowrap text-sm text-white/80">
                                    {reportType === 'adjudication' ? routine.division : routine.category}
                                  </td>
                                  <td className="px-4 py-1.5 print:py-4 whitespace-nowrap text-sm text-white/80">
                                    {reportType === 'adjudication' ? routine.category : routine.division}
                                  </td>
                                </motion.tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    );
                  }
                })()}
              </StageCard>
                  </>
                );
              })()}
            </>
          )}
        </div>
        </div>
      </div>
      )}
    </div>
  );
};

export default AwardsVisualizerPage;
